/**
 * Variant B — page-per-question questionnaire engine.
 * New-user flow only, WL DE (mention: weight_loss_2).
 * Renders one question/subquestion per page with branching via parent_id reveal,
 * browser-history back/forward, per-page save, BMI gate, and contraindication gate.
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import * as http from "../../../utils/http";
import {
  getForwardableFunnelSearch,
  getForwardableFunnelSearchString,
} from "../../../utils/funnelSearchParams";
import { useQuestionnaire } from "../customHooks/useQuestionnaire";
import {
  RenderAnswerGroup,
  bundleQuestionnaireForSending,
  formatQuestionnaire,
  isQuestionaireBlocking,
} from "../components/helpers";
import { calculateBMI, extractBmiThresholds, computeHasComorbidity } from "../components/BMISection/BMISection";
import BMISliderSection from "./BMISliderSection";
import { checkBmiInput, usesBmiInputLimits } from "../components/BMISection/bmiLimits";
import HeaderController from "../../../components/Headers/HeaderController";
import LoadingModal from "../../../components/Modals/LoadingModal/LoadingModal";
import ServerError from "../../../components/ServerError/ServerError";
import { MainTracker } from "../../../PixelTrackers/MainTracker";
import { track as trackGTM } from "../../../PixelTrackers/GtmTracker";
import {
  saveQuestionnaireAnswers,
  serializeQuestionnaireAnswers,
  computeRestorePlan,
  computePrefillPlan,
  hasSavedQuestionnaireProgress,
  markPendingClear,
  markSkipQuestionnaireAnsweredThisSession,
  wasSkipQuestionnaireAnsweredThisSession,
  wasQuestionnaireSkippedThisSession,
  FlattenedQuestion,
  AnswerValue,
  AnswerTreeNode,
  PrefilledAnswer,
} from "../../../utils/QuestionnaireAnswersStorage";
import { useCartContext } from "../../../customHooks/useCart";
import { useCustomer } from "../../../customHooks/useCustomer";
import { clientConfig } from "../../../config";
import IntroScreen from "./IntroScreen";
import QuestionFrame from "./QuestionFrame";
import { ScreenTransitionProvider, useScreenTransitionFromStack } from "./screenTransition";
import HealthProtectedModal from "./HealthProtectedModal";
import WhyWeAskModal from "./WhyWeAskModal";
import BlockerModal from "./BlockerModal";
import {
  BLOCKER_SHOWN_EVENT,
  BlockerId,
  getAnswerProps,
  getBlockingAnswerId,
  getBmiBand,
  getDevice,
  getLocale,
  getQuestionId,
  getQversion,
  QUESTION_ANSWERED_EVENT,
  QUESTION_ERROR_EVENT,
  QUESTIONNAIRE_SCREEN_VIEWED_EVENT,
  QuestionErrorType,
  SCREEN_IDS,
  SUMMARY_PAGE_VIEWED_EVENT,
  SUMMARY_CONTINUE_CLICKED_EVENT,
  SUMMARY_EDIT_CLICKED_EVENT,
} from "../../../utils/questionnaireEvents";
import { getCartMention, getCartMentionQuery } from "../../../utils/funnelMentionRedirects";
import { isPostCheckoutVerificationCountry } from "../../../utils/postCheckoutVerification";
import SingleSelectAnswers, { pinsSingleSelectAnswersToBottom } from "./SingleSelectAnswers";
import MultiSelectAnswers from "./MultiSelectAnswers";
import HealthConditionAnswers from "./HealthConditionAnswers";
import SignInModal from "./SignInModal";
import OAuthSignupCompletion, { isOAuthSignupCompletionUrl } from "./OAuthSignupCompletion";
import PrimaryCtaButton from "./PrimaryCtaButton";
import WeightLossProjectionPage, { WeightLossProjectionDisclaimer } from "./WeightLossProjectionPage";
import RealResultsPage from "./RealResultsPage";
import SocialProofPage, { preloadSocialProofPhotos } from "./SocialProofPage";
import HealthProfileSummaryPage, {
  SUMMARY_EDIT_SECTION_GOAL,
  summaryEditSectionForQuestion,
} from "./HealthProfileSummaryPage";
import HealthProfileEditModal, { HealthProfileDraft } from "./HealthProfileEditModal";
import AnswerEditModal from "./AnswerEditModal";
import ContactDetailsPage from "./ContactDetailsPage";
import { FunnelContext, ShellScopedId } from "./shellContext";
import LoadingPage from "./LoadingPage";
import {
  SkipQuestionnaireModal,
  SkipQuestionnaireHandle,
} from "../../../components/Modals/SkipQuestionnaireModal/SkipQuestionnaireModal";
import {
  calculateAge,
  getHealthSummaryEntries,
  HealthSummaryEntry,
} from "./healthSummaryHelpers";
import * as translator from "../../../utils/translator";
import { projectedGoalWeight } from "./constants";
import {
  SpecialPageType,
  ScreenDescriptor,
  PageDescriptor,
  pageKey,
  nextSubIdx,
  computeNextPage,
  estimateRemainingPages,
  findConsentBlock,
  firstAnswerablePage,
  getConsentAnswerHtml,
  resumePageHistory,
  isPageAnswered,
  computeRevealedForSubmit,
  getPageContent,
  highlightRevealingDiseaseName,
  genderOfAnswer,
  isQ1ReturningPatient,
  isBlockerAnswer,
  isBmiComorbidityQuestion,
  hasSelectedComorbidity,
  getPaginatableAdditional,
  isHealthConditionNothingChanged,
  getAuthGatedSkipIdxs,
  toScreen,
  gateEmailStepBeforeConsent,
  restoreEmailStepAfterOAuth,
  isWeightLossReasonBlock,
} from "./navigationHelpers";
import {
  registerQuestionnaireVersionSuperProperty,
  hasGivenWlEmail,
  markWlEmailGiven,
  peekWlEmailStepOAuthReturn,
  clearWlEmailStepOAuthReturn,
} from "../../../utils/wlEmailStepExperiment";
import { getWlSummaryFunnel } from "../../../utils/wlSummaryEditExperiment";

const t = (language: string | undefined, key: string): string =>
  translator.translate(language, "default", "QuestionnairePage/" + key) || "";

const HERO_DESKTOP = `${clientConfig.IMAGES_URL}redesign_2025/care-plus/questionnaire/care_plus_frame_2/care_plus_frame_2.png`;
const HERO_MOBILE = `${clientConfig.IMAGES_URL}redesign_2025/care-plus/questionnaire/care_plus_frame_2_mobile/care_plus_frame_2_mobile.png`;
const DISCOUNT_BADGE_DESKTOP = `${clientConfig.IMAGES_URL}redesign_2025/care-plus/questionnaire/questionnaire_discount_badge/questionnaire_discount_badge.svg`;
const DISCOUNT_BADGE_MOBILE = `${clientConfig.IMAGES_URL}redesign_2025/care-plus/questionnaire/questionnaire_discount_badge/questionnaire_discount_badge.svg`;
// The submit loader stays at least this long, even when the request is faster, so it reads as processing.
const MIN_SUBMIT_LOADING_MS = 3000;

// ---------- component ----------

const QuestionnaireVariantB: React.FC = () => {
  const { country, language, category: mention } = useParams<{
    country: string;
    language: string;
    category: string;
  }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { saveCart } = useCartContext();

  // ---- questionnaire data ----
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [flattenedQuestionnaire, setFlattenedQuestionnaire] = useState<any[]>([]);
  const [categoryID, setCategoryID] = useState<number | null>(null);
  const [categoryMention, setCategoryMention] = useState<string>("");
  const [, setCategoryMixpanelTitle] = useState<string>("");
  const [, setCatalogSettings] = useState<any>({});
  // Same skip-to-treatment engine as Variant A — headless, driven by
  // health_condition "No / nothing changed" via the imperative handle.
  const skipQuestionnaireRef = useRef<SkipQuestionnaireHandle>(null);
  // HL-12059: popup already answered this session (read at load) → health_condition is not asked again.
  const [skipQuestionnaireAnsweredAtLoad, setSkipQuestionnaireAnsweredAtLoad] = useState(false);

  // ---- answer state (delegated to useQuestionnaire) ----
  const questionnaireAttributes = useQuestionnaire();
  const [filesMap, setFilesMap] = useState<Record<number, any[]>>({});

  // ---- stepper state (unified navigation stack) ----
  // Every screen — question pages and end-of-flow overlays alike — is one
  // entry on this stack. Forward navigation always pushes exactly one entry
  // (`pushScreen`); backward navigation always pops exactly one, via the
  // browser's own history (`handleBack` → `window.history.back()` → the
  // popstate listener below, which is the *only* place `screenHistory` is
  // ever set). `pageHistory`, `currentPage`, `specialPage`,
  // `showWeightProjection`, `showRealResults`, and `showHealthSummary` are
  // all derived reads off it, not independent state.
  const [screenHistory, setScreenHistory] = useState<ScreenDescriptor[]>([
    { type: "page", page: { questionIdx: 0, subIdx: null } },
  ]);
  const [showSubmitLoading, setShowSubmitLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // HL-11960: a returning user with saved progress for this country should see
  // the loader, not the Start screen, until the resumed stack is built —
  // otherwise the intro flashes before they are put back where they were.
  // The check is a pre-load approximation (the category id is not known yet);
  // buildInitialHistory restores the intro if nothing is actually resumed.
  const introDeferredForResumeRef = useRef(hasSavedQuestionnaireProgress(country));
  // Coming back from a Google redirect that started on the HL-11800 email step:
  // same treatment, the user is put back on the email step / its continuation.
  const [showIntro, setShowIntro] = useState(
    () => !peekWlEmailStepOAuthReturn() && !introDeferredForResumeRef.current
  );
  const [showHealthModal, setShowHealthModal] = useState(false);
  const healthModalShownRef = useRef(false);
  const [showFullLegal, setShowFullLegal] = useState(false);
  const [isWhyWeAskOpen, setIsWhyWeAskOpen] = useState(false);
  const [whyWeAskContent, setWhyWeAskContent] = useState<string | undefined>(undefined);
  // Hard-stop blocker (e.g. under-18 DOB). Dismiss returns to the same question.
  const [showBlockerModal, setShowBlockerModal] = useState(false);
  // Bumped on each failed BMI Continue so the slider shake animation restarts.
  const [bmiValidationAttempt, setBmiValidationAttempt] = useState(0);

  // ---- Q1 "Yes" branch: returning-patient fast-track ----
  const [showSignInModal, setShowSignInModal] = useState(false);
  // Error surfaced inside SignInModal from outside it (a failed Google sign-up completion).
  const [signInModalError, setSignInModalError] = useState<string | null>(null);

  // ---- Google sign-up for an email DoktorABC doesn't know yet ----
  // The API sends such a user back here with `showOauthAdditionalInfoModal=true`
  // plus the temp profile/tokens in the URL. The birthday/gender popup that
  // finishes the signup is rendered at the top level (below), NOT inside
  // SignInModal: nested in there it mounted in the same commit as its parent
  // portal and ended up *under* the sign-in dimmer, so the user saw the login
  // again on every attempt and could never register (both from the in-flow
  // "Sign in" and from the HL-11800 email step's Google button).
  // Snapshot of the arrival query string: the popup mounts late (after the
  // loading branch, possibly over the intro) and the URL is cleaned right below,
  // so it must not read `location.search` itself.
  const [oauthSignupParams] = useState(() => new URLSearchParams(window.location.search));
  const [showOAuthSignupCompletion, setShowOAuthSignupCompletion] = useState(
    () => isOAuthSignupCompletionUrl(oauthSignupParams)
  );
  // Drop the OAuth params from the address bar straight away: they carry a
  // one-shot token, a reload must not replay the popup, and the next Google
  // attempt's `cameFromPath` (built from location.search) must not carry stale
  // duplicates that would shadow the fresh ones. replaceState keeps the
  // variantBDepth history state intact.
  useEffect(() => {
    if (!showOAuthSignupCompletion) return;
    const forwardable = getForwardableFunnelSearch(oauthSignupParams);
    // The sign-up leg also carries Google's own token, which the shared strip list
    // doesn't know about; drop it here rather than widening that list for every funnel.
    forwardable.delete("externalAccessToken");
    const cleaned = forwardable.toString();
    if (cleaned === oauthSignupParams.toString()) return;
    const url = `${window.location.pathname}${cleaned ? `?${cleaned}` : ""}${window.location.hash}`;
    window.history.replaceState(window.history.state, "", url);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // When sign-in opens mid-flow, hold the next screen until the modal is
  // dismissed so the current question stays visible behind the overlay.
  const pendingAfterSignInRef = useRef<ScreenDescriptor | null>(null);
  const { customerState } = useCustomer();

  // ---- HL-11800 / HL-11986 wl_email_step design A/B/C ----
  // Guests always get the step before Legal Consent; `emailGiven` is the per-tab
  // "already captured" flag so the step is not shown twice in one funnel session
  // (reload included).
  const [emailGiven, setEmailGiven] = useState<boolean>(() => hasGivenWlEmail());
  // Set when this mount is the return leg of a Google OAuth redirect started on
  // the email step (ContactDetailsPage writes the marker before redirecting).
  // Peeked (not consumed) in the initializer so StrictMode's double call is
  // harmless; the load effect clears the storage and buildInitialHistory uses
  // the ref — for both the initial build and the delayed-login rebuild — then
  // nulls it so nothing re-applies it.
  const [oauthReturnAtMount] = useState<boolean>(() => peekWlEmailStepOAuthReturn());
  const emailStepOAuthReturnRef = useRef<boolean>(oauthReturnAtMount);

  // ---- HL-12029 Health Summary "edit answers" (arm B, 100% since HL-12595) ----
  // `edited` on summary_continue_clicked: any pop-up / modal save on the summary so far.
  const [summaryEdited, setSummaryEdited] = useState(false);
  const [showHealthProfileEdit, setShowHealthProfileEdit] = useState(false);
  // Index (flattenedQuestionnaire) of the question open in the answer pop-up, or null.
  const [editingQuestionIdx, setEditingQuestionIdx] = useState<number | null>(null);
  // Answers + validation as they were when the pop-up opened. The pop-up writes straight
  // into the questionnaire state through the regular handlers (so sibling / exclusive-none
  // rules stay the questionnaire's), Schließen restores this, Speichern drops it. The summary
  // behind the pop-up renders from the snapshot so nothing changes until Save (AC-4).
  const editSnapshotRef = useRef<{
    answers: Record<string | number, any>;
    validation: Record<string | number, any>;
  } | null>(null);

  const currentScreen = screenHistory[screenHistory.length - 1];
  const pageHistory = screenHistory
    .filter((s): s is Extract<ScreenDescriptor, { type: "page" }> => s.type === "page")
    .map((s) => s.page);
  const currentPage = pageHistory[pageHistory.length - 1];
  const specialPage: SpecialPageType = currentScreen.type === "special" ? currentScreen.special : null;
  const showWeightProjection = currentScreen.type === "weightProjection";
  const showRealResults = currentScreen.type === "realResults";
  const showSocialProof = currentScreen.type === "socialProof";
  const showHealthSummary = currentScreen.type === "healthSummary";
  // Slide direction for QuestionFrame's content, derived from each stack change.
  const screenTransition = useScreenTransitionFromStack(screenHistory, showIntro);

  const saveDebounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Flips true the first time the user genuinely moves forward (any pushScreen).
  // Used to gate the post-login history rebuild: a delayed OAuth login may only
  // rebuild the initial history while the user hasn't advanced past it yet —
  // once they have, rebuilding would clobber real progress.
  const userAdvancedRef = useRef(false);

  // Pushes one new screen forward. Reads `screenHistory.length` from this
  // render's closure (not inside the setState updater) so the depth stamped
  // into browser history can't double-increment under React StrictMode's
  // dev-mode double-invocation of updater functions.
  const pushScreen = (screen: ScreenDescriptor) => {
    userAdvancedRef.current = true;
    const depth = screenHistory.length + 1;
    window.history.pushState({ variantBDepth: depth }, "", window.location.href);
    setScreenHistory((prev) => [...prev, screen]);
  };

  // Consent questions: identified by `question.question_type` and folded onto
  // one merged "Legal Consent" page rendered at `anchorIdx`. `skipIdxs` are
  // removed from standalone pagination; `allIdxs` are the answers
  // auto-agreed on submit. See findConsentBlock.
  const {
    anchorIdx: legalConsentIdx,
    statementsIdx,
    fullLegalIdx,
    skipIdxs: consentSkipIdxs,
    allIdxs: consentAllIdxs,
  } = findConsentBlock(flattenedQuestionnaire);

  // health_condition is signed-in only (skip-to-treatment needs a prior order).
  // Guests never land on it — folded into the same skip list as consent.
  const isLoggedIn = Boolean(customerState?.customerID);
  // Live auth for the `[]` load effect, whose closure otherwise keeps the
  // customerState of the first render (a Google login resolving during the GET
  // would build a guest history first and only then rebuild as signed in).
  const isLoggedInRef = useRef(isLoggedIn);
  isLoggedInRef.current = isLoggedIn;
  // Reported by the headless SkipQuestionnaireModal; null until the check resolves.
  const [skipEligible, setSkipEligible] = useState<boolean | null>(null);
  // Same value for async handlers: set before submit() resolves, so it is current there.
  const skipEligibleRef = useRef<boolean | null>(null);
  const handleEligibilityChange = (eligible: boolean | null) => {
    skipEligibleRef.current = eligible;
    setSkipEligible(eligible);
  };
  const authGatedSkipIdxs = getAuthGatedSkipIdxs(flattenedQuestionnaire, isLoggedIn && !skipQuestionnaireAnsweredAtLoad, skipEligible);
  const navSkipIdxs = [...consentSkipIdxs, ...authGatedSkipIdxs];

  // Email step eligibility: guests who have not given an email this session.
  // It sits in front of the Legal Consent page for every design arm (via
  // resolveNextScreen on every forward-navigation site); the arm only changes
  // how ContactDetailsPage looks.
  const emailBeforeConsent = !isLoggedIn && !emailGiven;
  const resolveNextScreen = (next: ReturnType<typeof computeNextPage>): ScreenDescriptor =>
    gateEmailStepBeforeConsent(toScreen(next), { enabled: emailBeforeConsent, legalConsentIdx });

  // Makes the current history entry stand in for a freshly built stack of
  // `depth` screens: it is stamped with that depth, and `variantBCoveredFrom`
  // records the lowest depth it also covers because those screens have no entry
  // of their own. handleBack steps through the covered screens in-app.
  //
  // Never replay one pushState per screen here. This runs after a load, with no
  // user activation, and Chrome treats history entries added that way as
  // skippable: once the tab reaches its 50-entry history cap it prunes them
  // first to make room — the replayed entries themselves. Back then walked off
  // the few that survived and out of the funnel (resumed onto Legal Consent in
  // a long-lived tab: Back → Hausarzt, Back → the CMS category page).
  // replaceState never adds an entry, so there is nothing to prune.
  const coverHistoryUpTo = (depth: number) => {
    const state = window.history.state;
    const coveredFrom =
      typeof state?.variantBCoveredFrom === "number"
        ? state.variantBCoveredFrom
        : typeof state?.variantBDepth === "number"
          ? state.variantBDepth // a Start tap that raced the load: the intro keeps its own entry below
          : 0;
    window.history.replaceState(
      { variantBDepth: depth, variantBCoveredFrom: coveredFrom },
      "",
      window.location.href
    );
  };

  // Holds the fetched questionnaire payload so the initial-history builder
  // (below) can re-run once live auth state settles, without refetching.
  const loadedDataRef = useRef<any>(null);

  // Builds screenHistory from the loaded questionnaire + a given auth state.
  // Runs once with whatever auth state is known at fetch time, and again
  // (before the user has answered anything new) once a delayed OAuth login
  // resolves, so `loggedInAtLoad` isn't stuck stale from the pre-login race —
  // see useCustomer's synchronous session hydration vs. OAuthCallback's async
  // saveCustomer for why customerID can lag a beat after a Google redirect.
  // `emailStepBeforeConsent` (HL-11800, guests only) re-inserts the email
  // step when the resumed stack lands on the Legal Consent page, so a reload
  // there matches a fresh pass through the flow.
  // `oauthReturn` (HL-11800): this load is the return leg of a Google redirect
  // started on the email step. The resumed stack must reproduce the guest's
  // path up to the step — so the walk uses guest skips even when signed in
  // (otherwise it stops at the newly-unlocked, unanswered health_condition and
  // the user is thrown back to the start of the questionnaire) — and then ends
  // on the step itself (still a guest) or on its continuation (signed in).
  const buildInitialHistory = (
    data: any,
    loggedInAtLoad: boolean,
    emailStepBeforeConsent = false,
    oauthReturn = false
  ) => {
    const { skipIdxs: consentSkips, anchorIdx: consentAnchorIdx } = findConsentBlock(data.flattenedQuestionnaire);
    const skipQuestionnaireAnswered = wasSkipQuestionnaireAnsweredThisSession(data.categoryID);
    setSkipQuestionnaireAnsweredAtLoad(skipQuestionnaireAnswered);
    const loadSkipIdxs = [
      ...consentSkips,
      ...getAuthGatedSkipIdxs(data.flattenedQuestionnaire, loggedInAtLoad && !oauthReturn && !skipQuestionnaireAnswered),
    ];

    // Restore saved answers and resume at the first unanswered page.
    // Rebuild the answered-page stack so Back from that page still works.
    // Saves older than 30 days are dropped by computeRestorePlan.
    let startHistory: ScreenDescriptor[] = [
      { type: "page", page: firstAnswerablePage(data.flattenedQuestionnaire, loadSkipIdxs) },
    ];
    let resumed = false;
    if (country && typeof data.categoryID === "number") {
      const plan =
        computeRestorePlan(
          country,
          data.categoryID,
          data.flattenedQuestionnaire as FlattenedQuestion[],
          data.questionAnswersObject as Record<string | number, AnswerTreeNode>
        ) ??
        // HL-12059: skipped ("nothing changed") this session and came back — fill in the previous
        // order's answers, so Back walks through them and Continue returns to treatment selection.
        (wasQuestionnaireSkippedThisSession(data.categoryID)
          ? computePrefillPlan(
              data.prefilledQuestionnaire as PrefilledAnswer[],
              data.flattenedQuestionnaire as FlattenedQuestion[],
              data.questionAnswersObject as Record<string | number, AnswerTreeNode>
            )
          : null);
      if (plan) {
        const restoredMap: Record<string | number, AnswerValue> = {};
        for (const { node, value } of plan.valuesToApply) {
          restoredMap[node.recipe.id] = value;
        }
        questionnaireAttributes.setAnswersSelectedMap(
          (form: Record<string | number, AnswerValue>) => {
            const next = { ...form };
            for (const id of plan.idsToClear) delete next[id];
            for (const { node, value } of plan.valuesToApply) {
              next[node.recipe.id] = value;
            }
            return next;
          }
        );
        const path = resumePageHistory(data.flattenedQuestionnaire, restoredMap, loadSkipIdxs);
        startHistory = path.map((page) => ({ type: "page" as const, page }));

        // All questions answered (e.g. back from CT): land on the health
        // summary instead of the last question page (legal consent).
        const lastResumed = path[path.length - 1];
        if (
          lastResumed &&
          isPageAnswered(lastResumed, data.flattenedQuestionnaire, restoredMap) &&
          computeNextPage(lastResumed, data.flattenedQuestionnaire, restoredMap, loadSkipIdxs) === null
        ) {
          startHistory.push({ type: "healthSummary" });
        }

        // A returning patient has a full set of restored answers, so
        // resumePageHistory finds nothing unanswered and walks all the way to
        // the last reachable page — the consent anchor. That is right for
        // resuming an open questionnaire, but wrong straight off a login: the
        // whole point of unlocking health_condition is to ask it first. Trim
        // the resumed stack to end on it when it is reachable and unanswered.
        // Not on the OAuth return leg from the email step: the user was already
        // past that point and continues from the step, like the in-modal login.
        if (loggedInAtLoad && !oauthReturn && !skipQuestionnaireAnswered) {
          const hcIdx = data.flattenedQuestionnaire.findIndex(
            (q: any) => q?.question_type === "health_condition"
          );
          const hcAnswered =
            hcIdx >= 0 &&
            isPageAnswered(
              { questionIdx: hcIdx, subIdx: null },
              data.flattenedQuestionnaire,
              restoredMap
            );
          if (hcIdx >= 0 && !hcAnswered) {
            const hcPos = startHistory.findIndex(
              (s) => s.type === "page" && s.page.questionIdx === hcIdx
            );
            startHistory =
              hcPos >= 0
                ? startHistory.slice(0, hcPos + 1)
                : [...startHistory, { type: "page" as const, page: { questionIdx: hcIdx, subIdx: null } }];
          }
        }

        if (emailStepBeforeConsent && consentAnchorIdx !== null) {
          const last = startHistory[startHistory.length - 1];
          if (last?.type === "page" && last.page.questionIdx === consentAnchorIdx && last.page.subIdx === null) {
            startHistory = [...startHistory.slice(0, -1), { type: "contactDetails", next: last }];
          }
        }

        if (oauthReturn) {
          startHistory = restoreEmailStepAfterOAuth(startHistory, {
            loggedIn: loggedInAtLoad,
            legalConsentIdx: consentAnchorIdx,
          });
        }

        resumed = true;
      }
    }

    // A resume, a signed-in load, or the Google return leg from the email step
    // skips the Start screen and puts the user straight onto startHistory, so
    // Back has to be able to walk back through every screen on it — down to the
    // intro — even though none of them was reached by a tap in this visit (see
    // coverHistoryUpTo). Signed-in covers the brand-new patient off a Google
    // redirect too — the reload resets showIntro to its initial `true`, and
    // leaving it set drops them on the Start screen instead of the questionnaire.
    if (resumed || loggedInAtLoad || oauthReturn) {
      setShowIntro(false);
      coverHistoryUpTo(startHistory.length);
    } else if (introDeferredForResumeRef.current) {
      // HL-11960: the loader was shown on the strength of saved progress that
      // turned out not to apply (other category, expired, or a BO change broke
      // the signatures). Nothing to resume — the guest gets the Start screen
      // it would have seen anyway, just after the load instead of before it.
      setShowIntro(true);
    }
    setScreenHistory(startHistory);

    // Logged in (e.g. straight off a Google OAuth redirect): the landing
    // page may be the newly-unlocked health_condition question — check
    // skip-eligibility immediately, same as Variant A firing its popup as
    // soon as login + catalog are known, instead of waiting for a tap.
    const landingScreen = startHistory[startHistory.length - 1];
    const landingPage = landingScreen?.type === "page" ? landingScreen.page : null;
    const landingQuestion =
      landingPage && data.flattenedQuestionnaire[landingPage.questionIdx];
    if (loggedInAtLoad && landingQuestion?.question_type === "health_condition") {
      skipQuestionnaireRef.current?.submit();
    }

    // Signed in via Google from the email step: run the same returning-patient
    // skip-eligibility check every other login path runs (dismissSignInModal).
    // If it navigates to treatment selection the continuation above is moot;
    // otherwise the user simply stays on it. The marker is spent at this point;
    // a guest build keeps it so a delayed login can still rebuild onto the
    // continuation.
    if (oauthReturn && loggedInAtLoad) {
      skipQuestionnaireRef.current?.submit();
      emailStepOAuthReturnRef.current = false;
    }
  };

  // ---- load questionnaire ----
  useEffect(() => {
    if (!country || !language || !mention) return;

    http
      .getFromServer(
        `questionnaire/${country}/${language}/${mention}?customerVerificationRequired=${searchParams.get("cv") === "true"}${getCartMentionQuery(mention)}`,
        [http.MIDDLEWARES.CUSTOMER_ID, http.MIDDLEWARES.CART_ORDER]
      )
      .then((response) => {
        // Post-checkout questions are asked after purchase (My Account), never here — same rule as Questionnaire.tsx.
        const data = {
          ...response.data,
          flattenedQuestionnaire: (response.data.flattenedQuestionnaire ?? []).filter(
            (q: any) => !q.is_post_checkout_question
          ),
        };
        loadedDataRef.current = data;

        questionnaireAttributes.setQuestionnaireTree(data.questionAnswersObject);
        questionnaireAttributes.setFlattenedQuestionnaire(data.flattenedQuestionnaire);
        setFlattenedQuestionnaire(data.flattenedQuestionnaire);
        setCategoryID(data.categoryID);
        setCategoryMention(data.categoryMention);
        setCategoryMixpanelTitle(data.categoryMixpanelTitle);
        setCatalogSettings(JSON.parse(data.catalogSettings?.settings ?? "{}"));

        MainTracker.superProperties({
          "Category Catalog": data.categoryShortcut,
          "Mixpanel Category Catalog": data.categoryMixpanelTitle,
        });
        // HL-11986: questionnaire_version (wl | wl3) on every funnel event.
        registerQuestionnaireVersionSuperProperty(data.categoryMention ?? mention, country);

        // Auth at load time (customer may already be in session storage from a
        // prior session). If a Google OAuth login is still resolving
        // asynchronously post-redirect, the effect below re-runs this once
        // customerID arrives, before the user can answer anything.
        // Only guests get the email step; signed-in users never see it.
        const guestAtLoad = !isLoggedInRef.current;
        // HL-11800: the Google-return marker is single-use — off storage now,
        // kept in the ref for this build and a possible delayed-login rebuild.
        const oauthReturn = emailStepOAuthReturnRef.current;
        if (oauthReturn) clearWlEmailStepOAuthReturn();

        buildInitialHistory(data, !guestAtLoad, guestAtLoad && !hasGivenWlEmail(), oauthReturn);

        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-finalize the initial history once, the moment a delayed post-redirect
  // login resolves (Google OAuth: customerID is set asynchronously via
  // OAuthCallback's saveCustomer, after this component has already mounted
  // and may have built its initial history as a guest).
  const loginSettledRef = useRef(isLoggedIn);
  useEffect(() => {
    if (loginSettledRef.current === isLoggedIn) return;
    loginSettledRef.current = isLoggedIn;
    if (!isLoggedIn || !loadedDataRef.current) return;
    // Only rebuild while the user hasn't manually moved forward yet. A restored
    // guest history can be multi-page (deep-linked resume), so we can't gate on
    // screenHistory.length here — that would block the rebuild that unlocks
    // health_condition. userAdvancedRef flips only on a real pushScreen.
    if (userAdvancedRef.current) return;
    // Still carries the email-step Google-return marker if the guest build
    // consumed it moments ago, so the signed-in rebuild lands on the
    // continuation instead of the questionnaire start.
    buildInitialHistory(loadedDataRef.current, true, false, emailStepOAuthReturnRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  // A signed-in load can land on health_condition before the skip check resolves.
  // If it comes back not eligible, swap that screen for the next one in place.
  // Only on the check's result, not on later screen changes (Back onto it stays put).
  // Not while a tap's submit is in flight: handleHealthConditionSelect advances itself.
  useEffect(() => {
    if (skipEligible !== false || submitting || currentScreen?.type !== "page") return;
    if (flattenedQuestionnaire[currentScreen.page.questionIdx]?.question_type !== "health_condition") return;
    const next = computeNextPage(
      currentScreen.page,
      flattenedQuestionnaire,
      questionnaireAttributes.answersSelectedMap,
      navSkipIdxs
    );
    setScreenHistory((prev) => [...prev.slice(0, -1), resolveNextScreen(next)]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skipEligible]);

  // ---- History API: browser back pops exactly one screen ----
  // This is the *only* place screenHistory is ever set from a "go back"
  // action — handleBack below never touches state directly, it only calls
  // window.history.back(), so there is a single, unambiguous code path for
  // backward navigation instead of a direct-state-set/popstate dual path.
  //
  // screenHistoryRef keeps the latest stack for popstate (avoids stale
  // closures) and lets us fall back to "pop one" when state is missing.
  const screenHistoryRef = useRef(screenHistory);
  screenHistoryRef.current = screenHistory;

  useEffect(() => {
    const onPopState = (e: PopStateEvent) => {
      const depth = e.state?.variantBDepth;

      // Intro baseline.
      if (depth === 0) {
        setShowIntro(true);
        return;
      }

      // Prefer stamped depth (one entry per pushScreen / intro start).
      if (typeof depth === "number" && depth > 0) {
        setShowIntro(false);
        setScreenHistory((prev) => {
          if (depth >= prev.length) return prev;
          return prev.slice(0, Math.max(depth, 1));
        });
        return;
      }

      // Fallback: history entry without variantBDepth (e.g. external). Pop one
      // screen if we can; otherwise leave stack alone.
      setScreenHistory((prev) => (prev.length > 1 ? prev.slice(0, -1) : prev));
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  // ---- seed the baseline history entry exactly once ----
  // depth 0 = intro screen, depth 1 = first question — the same numbering
  // pushScreen uses for every later screen, so there is only ever one entry
  // per depth and popping back always lands on the correct prior screen.
  // Uses replaceState (never pushState) so this doesn't create a duplicate
  // top-of-stack entry — that duplication was the root cause of the old
  // "back needs 2-3 clicks" bug. Runs on mount (not gated on `loading`) so
  // it's always in place before the user can click "Start".
  const initialHistoryDoneRef = useRef(false);
  useEffect(() => {
    if (initialHistoryDoneRef.current) return;
    initialHistoryDoneRef.current = true;
    window.history.replaceState({ variantBDepth: 0 }, "", window.location.href);
  }, []);

  // ---- HL-11440 §11 analytics ----
  // qversion: "v2" = the weight_loss_3 short questionnaire, "control" = everything else. Tracking only (DE);
  // the WL3 design follows `funnel`, which also knows the UK WL3 arm (weight_loss_2).
  const qversion = getQversion(categoryMention || mention);
  // HL-12264: the WL3 arm per country (DE weight_loss_3, UK weight_loss_2). The UK sends no qversion.
  const isUkFunnel = country?.toUpperCase() === "GB";
  const funnel = getWlSummaryFunnel(categoryMention || mention, country);
  // Same signal as qversion, so the tracking ids and the analytics agree on which funnel this is.
  const inFunnel = (node: React.ReactElement) => (
    <FunnelContext.Provider value={funnel}>{node}</FunnelContext.Provider>
  );
  // HL-12357: the social-proof photos are fetched while the user answers the questions before them.
  useEffect(() => {
    if (funnel === "wl3") preloadSocialProofPhotos("wl3");
  }, [funnel]);
  useEffect(() => {
    document.documentElement.classList.add("wl-scrollbar");
    return () => document.documentElement.classList.remove("wl-scrollbar");
  }, []);
  // Started when a screen renders, read when the user leaves it via Next.
  const screenShownAtRef = useRef<number>(Date.now());
  // Failed Next attempts on the current screen; reset on every screen change.
  const attemptCountRef = useRef<number>(0);

  // Identity of what is on screen right now: a DB question / sub-page id, or a
  // fixed slug for interstitials (intro, weight-projection, ...).
  const currentQuestionId = (): string | undefined => {
    if (showIntro) return SCREEN_IDS.intro;
    if (currentScreen.type !== "page") return SCREEN_IDS[currentScreen.type];
    return getQuestionId(getPageContent(currentPage, flattenedQuestionnaire));
  };
  const currentQuestionType = (): string | undefined => {
    if (showIntro) return "intro";
    if (currentScreen.type !== "page") return currentScreen.type;
    return getPageContent(currentPage, flattenedQuestionnaire)?.questionType ?? undefined;
  };
  // Every question page visited so far, oldest first — the branch the patient took.
  const branchPath = (): string =>
    pageHistory
      .map((page) => getQuestionId(getPageContent(page, flattenedQuestionnaire)))
      .filter(Boolean)
      .join(">");

  const baseEventProps = () => ({
    ...(isUkFunnel ? {} : { qversion }),
    question_id: currentQuestionId(),
    screen_number: showIntro ? 0 : screenHistory.length,
  });

  const trackQuestionError = (error_type: QuestionErrorType, retry_count = 0) => {
    if (error_type === "validation") attemptCountRef.current += 1;
    MainTracker.track("custom", QUESTION_ERROR_EVENT, { ...baseEventProps(), error_type, retry_count });
  };

  const trackQuestionAnswered = (answersMap: Record<string | number, any>) => {
    const pageContent = getPageContent(currentPage, flattenedQuestionnaire);
    MainTracker.track("custom", QUESTION_ANSWERED_EVENT, {
      ...baseEventProps(),
      ...getAnswerProps(pageContent, answersMap),
      time_on_question_ms: Date.now() - screenShownAtRef.current,
      attempt_count: attemptCountRef.current + 1,
    });
  };

  const trackBlockerShown = (
    blocker_id: BlockerId,
    extra: { blocking_answer_id?: number | string; bmi_value?: number | null } = {}
  ) => {
    const bmi = extra.bmi_value ?? undefined;
    MainTracker.track("custom", BLOCKER_SHOWN_EVENT, {
      ...baseEventProps(),
      blocker_id,
      blocking_answer_id: extra.blocking_answer_id,
      bmi_value: bmi === undefined || bmi === null ? undefined : Math.round(bmi * 10) / 10,
      bmi_band: getBmiBand(bmi),
    });
  };

  // "questionnaire_screen_viewed" fires once per screen — questions, follow-up
  // sub-pages and interstitials alike — as soon as its content is on screen.
  // The intro shows while the GET is still in flight, so it is not gated on `loading`.
  const viewedScreenKey = showIntro
    ? "intro"
    : currentScreen.type === "page"
      ? pageKey(currentPage)
      : currentScreen.type;
  // `loading` is a dep only so the first question fires once the GET lands; the
  // ref stops the intro (or any screen) from being counted twice when it flips.
  const lastViewedKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!showIntro && (loading || flattenedQuestionnaire.length === 0)) return;
    if (!showIntro && currentScreen.type === "page" && !getPageContent(currentPage, flattenedQuestionnaire)) return;
    if (lastViewedKeyRef.current === viewedScreenKey) return;
    lastViewedKeyRef.current = viewedScreenKey;
    screenShownAtRef.current = Date.now();
    attemptCountRef.current = 0;
    MainTracker.track("custom", QUESTIONNAIRE_SCREEN_VIEWED_EVENT, {
      ...baseEventProps(),
      question_type: currentQuestionType(),
      branch_path: branchPath(),
      is_returning: isLoggedIn,
      device: getDevice(),
      locale: getLocale(language, country),
    });
    // Pre-HL-11440 per-question event (PR #6884), kept firing unchanged so existing
    // Mixpanel reports built on it keep their data. Question pages only, same payload.
    if (!showIntro && currentScreen.type === "page") {
      const pageContent = getPageContent(currentPage, flattenedQuestionnaire);
      if (pageContent) {
        MainTracker.track("custom", "Questionnaire Question Viewed", { question_title: pageContent.text });
      }
    }
  }, [viewedScreenKey, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  // HL-12029 §4: once per arrival on the Health Summary.
  useEffect(() => {
    if (!showHealthSummary || loading) return;
    MainTracker.track("custom", SUMMARY_PAGE_VIEWED_EVENT, summaryEventProps());
  }, [showHealthSummary, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- debounced save to localStorage ----
  // `answersOverride`: the map as it will be once setState calls made in the same tick land
  // (the timer otherwise saves this render's answers, i.e. the pre-change map).
  const scheduleSave = useCallback((answersOverride?: Record<string | number, any>) => {
    if (!categoryID || !country) return;
    if (saveDebounceTimerRef.current !== null) clearTimeout(saveDebounceTimerRef.current);
    saveDebounceTimerRef.current = setTimeout(() => {
      saveDebounceTimerRef.current = null;
      const questions = serializeQuestionnaireAnswers(
        flattenedQuestionnaire as FlattenedQuestion[],
        (answersOverride ?? questionnaireAttributes.answersSelectedMap) as Record<string | number, AnswerValue | null | undefined>
      );
      try {
        saveQuestionnaireAnswers({
          countryID: country!,
          categoryID: categoryID!,
          categoryMention,
          questions,
        });
      } catch {
        // localStorage blocked/full — the answer stays in React state, only the resume copy is lost.
        trackQuestionError("save_failed");
      }
    }, 400);
  }, [categoryID, country, categoryMention, flattenedQuestionnaire, questionnaireAttributes.answersSelectedMap]);

  // ---- answer handlers ----
  const onAnswerClick = (answer: any, value?: any) => {
    const valueForSaving = value !== undefined && value !== null ? value : true;
    questionnaireAttributes.setAnswersSelectedMap((form: any) => ({
      ...form,
      [answer.recipe.id]: valueForSaving,
    }));
    questionnaireAttributes.setQuestionnaireValidationObject((oldVal: any) => {
      const isValid =
        answer.recipe.validation_rule?.max_value !== undefined
          ? answer.recipe.validation_rule.min_value <= Number(value) &&
            Number(value) <= answer.recipe.validation_rule.max_value
          : true;
      const idToUpdate =
        answer.recipe.type === "number" ? answer.recipe.id : answer.recipe.parent_id;
      return { ...oldVal, [idToUpdate]: isValid };
    });
    questionnaireAttributes.shutDownSiblingsDescendants(answer);
  };

  const onTextChange = (
    answerId: number,
    text: string | null,
    validationRule: any,
    recipeQuestionId: number | null
  ) => {
    let isInputValid = true;
    if (text === null || text === "") isInputValid = false;
    if (validationRule?.max_value !== undefined)
      isInputValid = Number(text) <= validationRule.max_value;
    if (validationRule?.min_value !== undefined)
      isInputValid = isInputValid && validationRule.min_value <= Number(text);
    questionnaireAttributes.setAnswersSelectedMap((form: any) => ({
      ...form,
      [answerId]: text,
    }));
    questionnaireAttributes.setQuestionnaireValidationObject((oldVal: any) => ({
      ...oldVal,
      [answerId]: isInputValid,
    }));
  };

  // no-ops: we don't use mount/unmount lifecycle to drive revealedAnswerGroups in Variant B
  const noOpReveal = () => {};
  const noOpHide = () => {};

  // ---- validate current page ----
  const validateCurrentPage = (): boolean => {
    const pageContent = getPageContent(currentPage, flattenedQuestionnaire);
    if (!pageContent) return true;
    const { answerGroup, questionType } = pageContent;
    if (!answerGroup?.recipe?.id) return true;
    if (questionType === "title") return true;

    const answersMap = questionnaireAttributes.answersSelectedMap;
    const validationObj = questionnaireAttributes.questionnaireValidationObject;
    let isValid = true;

    if (answerGroup.recipe.type.includes("check_list")) {
      isValid = answerGroup.children?.some((child: any) => answersMap[child.recipe.id]);
    } else if (["number", "textarea", "text", "date"].includes(answerGroup.recipe.type)) {
      isValid =
        !!answersMap[answerGroup.recipe.id] && validationObj[answerGroup.recipe.id] !== false;
    } else {
      isValid = answerGroup.children?.some((child: any) => answersMap[child.recipe.id]);
    }

    if (!isValid) {
      questionnaireAttributes.setQuestionnaireValidationObject((oldVal: any) => ({
        ...oldVal,
        [answerGroup.recipe.id]: false,
      }));
    }
    return isValid;
  };

  // Same rule as validateCurrentPage, for any answer group (HL-12029 pop-up: root + revealed follow-ups).
  const isAnswerGroupValid = (
    answerGroup: any,
    answersMap: Record<string | number, any>,
    validationObj: Record<string | number, any>
  ): boolean => {
    if (!answerGroup?.recipe?.id) return true;
    const type = String(answerGroup.recipe.type || "");
    if (type.includes("check_list")) {
      return !!answerGroup.children?.some((child: any) => answersMap[child.recipe.id]);
    }
    if (["number", "textarea", "text", "date"].includes(type)) {
      return !!answersMap[answerGroup.recipe.id] && validationObj[answerGroup.recipe.id] !== false;
    }
    return !!answerGroup.children?.some((child: any) => answersMap[child.recipe.id]);
  };

  // ---- HL-12029: edit answers from the Health Summary ----
  const summaryFunnel = funnel;

  const summaryEventProps = () => ({ funnel: summaryFunnel });

  // The answers the summary shows: the pre-edit snapshot while a pop-up is open.
  const answersForSummary = (): Record<string | number, any> =>
    editSnapshotRef.current?.answers ?? questionnaireAttributes.answersSelectedMap;

  // DOB / height / weight answer nodes, shared by the summary chips and the profile modal.
  const getHealthProfileRefs = () => {
    const bmiQuestion = flattenedQuestionnaire.find((q: any) => q.question_type === "bmi_question");
    const heightNode = bmiQuestion?.answersGroup;
    const weightNode = bmiQuestion?.answersGroup?.children?.[0];
    // Same DOB rule as the under-18 gate (isBirthdayPage): tagged "birthday", or else the
    // first top-level date answer — most catalogs leave the DOB question untagged.
    const birthdayQuestion =
      flattenedQuestionnaire.find((q: any) => q.question_type === "birthday") ??
      flattenedQuestionnaire.find((q: any) => q.answersGroup?.recipe?.type === "date");
    const birthdayNode = birthdayQuestion?.answersGroup;
    return { bmiQuestion, heightNode, weightNode, birthdayNode };
  };

  // Opens the pop-up for a question. `answersOverride` is the map as it will be once
  // pending setState calls land (a save in the same tick), so the snapshot is not stale.
  const beginAnswerEdit = (
    questionIdx: number,
    answersOverride?: Record<string | number, any>,
    validationOverride?: Record<string | number, any>
  ) => {
    editSnapshotRef.current = {
      answers: { ...(answersOverride ?? questionnaireAttributes.answersSelectedMap) },
      validation: { ...(validationOverride ?? questionnaireAttributes.questionnaireValidationObject) },
    };
    setEditingQuestionIdx(questionIdx);
  };

  const handleSummaryEditEntry = (entry: HealthSummaryEntry) => {
    const questionIdx = flattenedQuestionnaire.findIndex((q: any) => q.id === entry.questionId);
    if (questionIdx < 0) return;
    MainTracker.track("custom", SUMMARY_EDIT_CLICKED_EVENT, {
      ...summaryEventProps(),
      section: summaryEditSectionForQuestion(entry.questionId),
    });
    beginAnswerEdit(questionIdx);
  };

  // Schließen / X: every change since open is dropped (AC-11).
  const discardAnswerEdit = () => {
    const snapshot = editSnapshotRef.current;
    if (snapshot) {
      questionnaireAttributes.setAnswersSelectedMap(snapshot.answers);
      questionnaireAttributes.setQuestionnaireValidationObject(snapshot.validation);
    }
    editSnapshotRef.current = null;
    setEditingQuestionIdx(null);
  };

  // Root answer group plus the follow-ups its current answers reveal — what the pop-up
  // shows and what Speichern validates.
  type EditablePageContent = NonNullable<ReturnType<typeof getPageContent>>;
  const editablePageContents = (questionIdx: number, answersMap: Record<string | number, any>) => {
    const question = flattenedQuestionnaire[questionIdx];
    const root = getPageContent({ questionIdx, subIdx: null }, flattenedQuestionnaire);
    if (!question || !root) return [];
    const revealed = getPaginatableAdditional(question)
      .map((subq: any, subIdx: number) => ({ subq, subIdx }))
      .filter(({ subq }) => subq.recipe?.parent_id && answersMap[subq.recipe.parent_id])
      .map(({ subIdx }) => getPageContent({ questionIdx, subIdx }, flattenedQuestionnaire))
      .filter((content): content is EditablePageContent => content !== null);
    return [root, ...revealed];
  };

  // Speichern: same validation and hard-stop rules as the question page (AC-5); the
  // pop-up stays open on either so the answer can be corrected.
  const saveAnswerEdit = () => {
    if (editingQuestionIdx === null) return;
    const answersMap = questionnaireAttributes.answersSelectedMap;
    const validationObj = questionnaireAttributes.questionnaireValidationObject;
    const contents = editablePageContents(editingQuestionIdx, answersMap);

    const invalid = contents.filter((content) => !isAnswerGroupValid(content.answerGroup, answersMap, validationObj));
    if (invalid.length) {
      questionnaireAttributes.setQuestionnaireValidationObject((oldVal: any) => ({
        ...oldVal,
        ...Object.fromEntries(invalid.map((content) => [content.answerGroup.recipe.id, false])),
      }));
      trackQuestionError("validation");
      return;
    }

    const blocking = contents.find((content) => isBlockerAnswer(content, answersMap));
    if (blocking) {
      trackBlockerShown("B3", { blocking_answer_id: getBlockingAnswerId(blocking, answersMap) });
      setShowBlockerModal(true);
      return;
    }
    if (contents.some((content) => isBmiComorbidityBlocked(content, answersMap))) {
      trackBlockerShown("B2", { bmi_value: answeredBmi(answersMap) });
      setShowBlockerModal(true);
      return;
    }

    editSnapshotRef.current = null;
    setEditingQuestionIdx(null);
    setSummaryEdited(true);
    scheduleSave();
  };

  const handleSummaryEditGoal = () => {
    MainTracker.track("custom", SUMMARY_EDIT_CLICKED_EVENT, { ...summaryEventProps(), section: SUMMARY_EDIT_SECTION_GOAL });
    setShowHealthProfileEdit(true);
  };

  // Speichern in the profile modal: write the three answers through the regular handler
  // (so the doctor's copy and the resume copy both see them — AC-13), then, for 27–29.9
  // without a comorbidity, re-ask the comorbidity question the BMI rule points at.
  const saveHealthProfileEdit = (draft: HealthProfileDraft) => {
    const { bmiQuestion, heightNode, weightNode, birthdayNode } = getHealthProfileRefs();
    const heightId = heightNode?.recipe?.id;
    const weightId = weightNode?.recipe?.id;
    const birthdayId = birthdayNode?.recipe?.id;
    if (!heightId || !weightId) return;

    const heightText = String(draft.heightCm);
    const weightText = String(draft.weightKg);
    onTextChange(heightId, heightText, heightNode.recipe.validation_rule, heightNode.recipe.recipe_question_id);
    onTextChange(weightId, weightText, weightNode.recipe.validation_rule, weightNode.recipe.recipe_question_id);
    if (birthdayId) {
      onTextChange(birthdayId, draft.birthday, birthdayNode.recipe.validation_rule, birthdayNode.recipe.recipe_question_id);
    }
    const nextAnswers: Record<string | number, any> = {
      ...questionnaireAttributes.answersSelectedMap,
      [heightId]: heightText,
      [weightId]: weightText,
      ...(birthdayId ? { [birthdayId]: draft.birthday } : {}),
    };
    setShowHealthProfileEdit(false);
    setSummaryEdited(true);
    scheduleSave(nextAnswers);

    if (draft.state !== "conditional") return;
    const comorbidityGroupId = bmiQuestion?.answersGroup?.recipe?.validation_rule?.bmi_comorbidity_answer_group_id;
    const comorbidityIdx = flattenedQuestionnaire.findIndex(
      (q: any) =>
        (comorbidityGroupId && q.answersGroup?.recipe?.id === comorbidityGroupId) ||
        q.answersGroup?.recipe?.validation_rule?.is_bmi_comorbidity
    );
    if (comorbidityIdx < 0) return;
    const nextValidation: Record<string | number, any> = {
      ...questionnaireAttributes.questionnaireValidationObject,
      [heightId]: true,
      [weightId]: true,
      ...(birthdayId ? { [birthdayId]: true } : {}),
    };
    beginAnswerEdit(comorbidityIdx, nextAnswers, nextValidation);
  };

  // ---- BMI gate: true when BMI is below the comorbidity threshold (hard block).
  // Stays on the BMI page — the Significance danger message already explains why;
  // no separate disqualification screen.
  const currentBmi = (): number | null => {
    const pageContent = getPageContent(currentPage, flattenedQuestionnaire);
    if (!pageContent || pageContent.questionType !== "bmi_question") return null;
    const { question } = pageContent;
    const heightId = question.answersGroup?.recipe?.id;
    const weightId = question.answersGroup?.children?.[0]?.recipe?.id;
    const heightCm = Number(questionnaireAttributes.answersSelectedMap[heightId]);
    const weightKg = Number(questionnaireAttributes.answersSelectedMap[weightId]);
    if (!heightCm || !weightKg) return null;
    return calculateBMI(weightKg, heightCm);
  };

  const isBmiBlocked = (): boolean => {
    const pageContent = getPageContent(currentPage, flattenedQuestionnaire);
    if (!pageContent || pageContent.questionType !== "bmi_question") return false;
    const bmi = currentBmi();
    if (bmi === null) return false;
    const { comorbidity } = extractBmiThresholds(pageContent.question);
    return bmi < comorbidity;
  };

  const isBmiInputOutOfRange = (): boolean => {
    const pageContent = getPageContent(currentPage, flattenedQuestionnaire);
    if (!pageContent || pageContent.questionType !== "bmi_question") return false;
    const { question } = pageContent;
    if (!usesBmiInputLimits(question)) return false;
    const answers = questionnaireAttributes.answersSelectedMap;
    const typed = (answerId: number) => String(answers[answerId] ?? "");
    return (
      checkBmiInput(typed(question.answersGroup?.recipe?.id), "height") === "hard" ||
      checkBmiInput(typed(question.answersGroup?.children?.[0]?.recipe?.id), "weight") === "hard"
    );
  };

  // B2 fires when the patient tries to continue past a blocked BMI card.
  const trackBmiBlocked = () => trackBlockerShown("B2", { bmi_value: currentBmi() });

  // ---- under-18 DOB gate: patient must be 18+ (hard stop via BlockerModal).
  // Birthday pages are either tagged question_type "birthday" OR a top-level
  // date answer (Variant B DOB is a plain date field — medication "since when"
  // dates live only as subquestions, so they are excluded).
  const isBirthdayPage = (pageContent: NonNullable<ReturnType<typeof getPageContent>>): boolean => {
    if (pageContent.questionType === "birthday") return true;
    if (String(pageContent.questionType || "").startsWith("subquestion_")) return false;
    return pageContent.answerGroup?.recipe?.type === "date";
  };

  // Returns true when the current page is DOB and the entered age is < 18.
  const isUnderageBlocked = (
    answersMap: Record<string | number, any> = questionnaireAttributes.answersSelectedMap
  ): boolean => {
    const pageContent = getPageContent(currentPage, flattenedQuestionnaire);
    if (!pageContent || !isBirthdayPage(pageContent)) return false;

    const birthdayId = pageContent.answerGroup?.recipe?.id;
    if (!birthdayId) return false;

    const age = calculateAge(answersMap[birthdayId]);
    return age !== null && age < 18;
  };

  // Opens the Figma blocker popup and keeps the user on the same question.
  // Returns true when the gate fired (caller should stop navigation).
  const checkUnderageGate = (
    answersMap?: Record<string | number, any>
  ): boolean => {
    if (!isUnderageBlocked(answersMap)) return false;
    const pageContent = getPageContent(currentPage, flattenedQuestionnaire);
    trackBlockerShown("B1", { blocking_answer_id: pageContent?.answerGroup?.recipe?.id });
    setShowBlockerModal(true);
    return true;
  };

  // ---- hard-stop answers (pregnancy Yes, other contraindications) → BlockerModal.
  // Stays on the same question so X / "Review answers" lets the user change it.
  // answersMapOverride is required for radio one-tap advance (state not committed yet).
  const checkContraindicationGate = (
    answersMap: Record<string | number, any> = questionnaireAttributes.answersSelectedMap
  ): boolean => {
    const pageContent = getPageContent(currentPage, flattenedQuestionnaire);
    if (!pageContent) return false;
    if (isBlockerAnswer(pageContent, answersMap)) {
      trackBlockerShown("B3", { blocking_answer_id: getBlockingAnswerId(pageContent, answersMap) });
      setShowBlockerModal(true);
      return true;
    }
    return false;
  };

  // ---- BMI between the comorbidity and minimum thresholds (27–29.9) needs a weight-related
  // illness: on the is_bmi_comorbidity question, no illness picked → BlockerModal.
  const answeredBmi = (answersMap: Record<string | number, any>): number | null => {
    const { heightNode, weightNode } = getHealthProfileRefs();
    return calculateBMI(Number(answersMap[weightNode?.recipe?.id]), Number(answersMap[heightNode?.recipe?.id]));
  };

  const isBmiComorbidityBlocked = (pageContent: any, answersMap: Record<string | number, any>): boolean => {
    if (!isBmiComorbidityQuestion(pageContent)) return false;
    const bmi = answeredBmi(answersMap);
    if (bmi === null) return false;
    const { comorbidity, minimum } = extractBmiThresholds(getHealthProfileRefs().bmiQuestion);
    return bmi >= comorbidity && bmi <= minimum && !hasSelectedComorbidity(pageContent.answerGroup, answersMap);
  };

  const checkBmiComorbidityGate = (
    answersMap: Record<string | number, any> = questionnaireAttributes.answersSelectedMap
  ): boolean => {
    if (!isBmiComorbidityBlocked(getPageContent(currentPage, flattenedQuestionnaire), answersMap)) return false;
    trackBlockerShown("B2", { bmi_value: answeredBmi(answersMap) });
    setShowBlockerModal(true);
    return true;
  };

  // ---- BMI page validity: blocks the weight-loss projection interstitial
  // from being reachable with a missing or out-of-range height/weight —
  // sharing the same "is this answer good" check as validateCurrentPage
  // instead of duplicating it. ----
  const isBmiPageValid = (): boolean => {
    const pageContent = getPageContent(currentPage, flattenedQuestionnaire);
    const answerGroup = pageContent?.answerGroup;
    const heightId = answerGroup?.recipe?.id;
    const weightId = answerGroup?.children?.[0]?.recipe?.id;
    if (!heightId || !weightId) return false;
    const answersMap = questionnaireAttributes.answersSelectedMap;
    const validationObj = questionnaireAttributes.questionnaireValidationObject;
    if (!answersMap[heightId] || !answersMap[weightId]) return false;
    if (validationObj[heightId] === false || validationObj[weightId] === false) return false;
    return true;
  };

  const handleBmiContinue = () => {
    if (!isBmiPageValid()) {
      // Mirror validateCurrentPage: mark the missing/invalid height & weight
      // answers so BMISliderSection can render errorMode (red + shake + message).
      const pageContent = getPageContent(currentPage, flattenedQuestionnaire);
      const answerGroup = pageContent?.answerGroup;
      const heightId = answerGroup?.recipe?.id;
      const weightId = answerGroup?.children?.[0]?.recipe?.id;
      if (!heightId || !weightId) return;
      const answersMap = questionnaireAttributes.answersSelectedMap;
      questionnaireAttributes.setQuestionnaireValidationObject((oldVal: any) => ({
        ...oldVal,
        [heightId]: !!answersMap[heightId] && oldVal[heightId] !== false,
        [weightId]: !!answersMap[weightId] && oldVal[weightId] !== false,
      }));
      setBmiValidationAttempt((n) => n + 1);
      trackQuestionError("validation");
      return;
    }
    // Hard block low BMI on this page — the in-page danger message is the only
    // feedback; do not navigate to weight projection or a separate screen.
    if (isBmiBlocked()) {
      trackBmiBlocked();
      return;
    }
    trackQuestionAnswered(questionnaireAttributes.answersSelectedMap);
    pushScreen({ type: "weightProjection" });
  };

  // ---- navigate forward ----
  // Optional answersMapOverride: used when the caller just wrote an answer in
  // the same tick (health_condition one-tap advance) and React state has not
  // re-rendered yet — computeNextPage / isQ1ReturningPatient must see the new map.
  const handleNext = (answersMapOverride?: Record<string | number, any>) => {
    const answersMap = answersMapOverride ?? questionnaireAttributes.answersSelectedMap;

    if (!answersMapOverride && !validateCurrentPage()) {
      trackQuestionError("validation");
      return;
    }
    if (isBmiBlocked()) {
      trackBmiBlocked();
      return;
    }
    if (checkUnderageGate(answersMap)) return;
    if (checkContraindicationGate(answersMap)) return;
    if (checkBmiComorbidityGate(answersMap)) return;

    // Social proof's CTA re-enters here on the same question page: it was already tracked and saved.
    if (currentScreen.type !== "socialProof") {
      trackQuestionAnswered(answersMap);
      scheduleSave();
    }

    const next = computeNextPage(
      currentPage,
      flattenedQuestionnaire,
      answersMap,
      navSkipIdxs
    );
    // Social proof follows "Why do you want to lose weight?" once its follow-ups (e.g. "Other" → reason) are done.
    if (
      currentScreen.type !== "socialProof" &&
      isWeightLossReasonBlock(flattenedQuestionnaire, currentPage.questionIdx) &&
      !(next && isWeightLossReasonBlock(flattenedQuestionnaire, next.questionIdx))
    ) {
      pushScreen({ type: "socialProof" });
      return;
    }
    // Arm B: the consent page is preceded by the email step (see resolveNextScreen).
    const nextScreen: ScreenDescriptor = resolveNextScreen(next);

    // Auto-open the sign-in modal right as the user leaves Q1's first revealed
    // subquestion, but only when Q1 was answered as a returning patient (WL2:
    // "Yes, I have taken GLP-1 before"; WL3: "No, not my first order") and the
    // user is still a guest — first-timers and logged-in users skip past this.
    // Uses `firstRevealedSubIdx` (the very first parent_id-triggered subquestion
    // of Q1) as the checkpoint because it is the earliest follow-up after the
    // initial Yes/No gate — if Q1 has no subquestions (all parent_ids unmatched),
    // firstRevealedSubIdx is null and the modal never fires, which is correct:
    // with nothing to gate, there is no returning-patient fast-track to offer.
    //
    // Important: do NOT navigate yet — keep the current question mounted under
    // the modal so the page remains visible behind the dimmer. Navigation runs
    // when the modal is dismissed (skip / close / login success).
    // HL-12886: the UK never auto-opens it.
    const q1 = flattenedQuestionnaire[pageHistory[0].questionIdx];
    const firstRevealedSubIdx = nextSubIdx(q1, null, answersMap);
    if (
      !isUkFunnel &&
      !isLoggedIn &&
      currentPage.questionIdx === pageHistory[0].questionIdx &&
      currentPage.subIdx === firstRevealedSubIdx &&
      isQ1ReturningPatient(flattenedQuestionnaire, pageHistory, answersMap)
    ) {
      pendingAfterSignInRef.current = nextScreen;
      setShowSignInModal(true);
      return;
    }

    pushScreen(nextScreen);
  };

  // Single-select (radio_buttons): selecting an option advances immediately —
  // no separate Continue. Gender answers still route via Real-Results.
  // answersMapOverride keeps parent_id branching in sync on the same tick.
  const handleSingleSelectSelect = (child: any, answerGroup: any) => {
    onAnswerClick(child, true);

    const siblings = answerGroup?.children || [];
    const nextMap: Record<string | number, any> = {
      ...questionnaireAttributes.answersSelectedMap,
    };
    for (const sibling of siblings) {
      nextMap[sibling.recipe.id] = sibling.recipe.id === child.recipe.id;
    }

    const gender = genderOfAnswer(child?.recipe?.value);
    if (gender) {
      pushScreen({ type: "realResults", gender });
    } else {
      handleNext(nextMap);
    }
  };

  const singleSelectAnswersFor = (answerGroup: any, helpBlock?: string) => {
    const answers = (
      <SingleSelectAnswers
        answersGroup={answerGroup}
        answersSelectedMap={questionnaireAttributes.answersSelectedMap}
        onSelect={(child: any) => handleSingleSelectSelect(child, answerGroup)}
        errorMode={questionnaireAttributes.questionnaireValidationObject[answerGroup.recipe.id] === false}
      />
    );
    return funnel === "wl3" && pinsSingleSelectAnswersToBottom(answerGroup, helpBlock)
      ? { pinned: answers, inline: null }
      : { pinned: undefined, inline: answers };
  };

  // health_condition path buttons: select + advance in one tap.
  // "Nothing changed" → Variant A SkipQuestionnaireModal.submit() (headless).
  // "Something changed" → continue full questionnaire.
  const handleHealthConditionSelect = async (child: any) => {
    onAnswerClick(child, true);

    const pageContent = getPageContent(currentPage, flattenedQuestionnaire);
    const siblings = pageContent?.answerGroup?.children || [];
    const nextMap: Record<string | number, any> = {
      ...questionnaireAttributes.answersSelectedMap,
    };
    for (const sibling of siblings) {
      nextMap[sibling.recipe.id] = sibling.recipe.id === child.recipe.id;
    }

    if (!isHealthConditionNothingChanged(child?.recipe?.value)) {
      // HL-12059: answered for this tab session, like the popup's "Yes" — not asked again on re-entry.
      markSkipQuestionnaireAnsweredThisSession(categoryID, false);
      handleNext(nextMap);
      return;
    }

    // Try to skip. Keep the health_condition question on screen while the
    // eligibility check / POST is in flight — do NOT flip on the full-page
    // LoadingPage here, or a failed skip flashes the loader and then snaps
    // to another screen.
    setSubmitting(true);
    const navigated = await skipQuestionnaireRef.current?.submit();
    if (navigated) {
      // Only now commit to the loading screen — submit() has started the
      // navigation to select_treatment / shipping, so the loader covers
      // exactly that transition.
      setShowSubmitLoading(true);
      return;
    }
    // Not skip-eligible (e.g. first order on this questionnaire) → just carry
    // on with the questionnaire from here. Step exactly one page forward with
    // computeNextPage; do NOT scan for the first *unanswered* page, because a
    // returning patient's restored answers make every later page look answered
    // and that scan jumps straight to the consent page.
    setSubmitting(false);
    handleNext(nextMap);
  };

  // Legal Consent "Continue": agree the lone answer of every consent question
  // folded onto the merged page (statements + full-legal + summary), then
  // advance like a normal page — to any remaining questions after the consent
  // block, or to the end-of-flow interstitials (Health Profile Summary →
  // Loading) when none remain. The actual submit fires later at the Health
  // Summary "Continue"; the agreed answers are already in the map by then. Do NOT
  // submit here, and do NOT jump straight to the summary, or the questions that
  // come after the consent block get skipped.
  const agreeAndContinue = () => {
    const idxs = consentAllIdxs.length > 0 ? consentAllIdxs : [currentPage.questionIdx];
    const consentAnswers: any[] = [];
    for (const idx of idxs) {
      const child = flattenedQuestionnaire[idx]?.answersGroup?.children?.[0];
      if (child) consentAnswers.push(child);
    }
    if (consentAnswers.length > 0) {
      questionnaireAttributes.setAnswersSelectedMap((form: any) => {
        const next = { ...form };
        for (const child of consentAnswers) next[child.recipe.id] = true;
        return next;
      });
    }
    trackQuestionAnswered(questionnaireAttributes.answersSelectedMap);
    scheduleSave();

    const next = computeNextPage(
      currentPage,
      flattenedQuestionnaire,
      questionnaireAttributes.answersSelectedMap,
      navSkipIdxs
    );
    pushScreen(resolveNextScreen(next));
  };

  // Close the sign-in modal. If navigation was deferred while it was open
  // (Q1 returning-patient gate), advance to that pending screen once dismissed
  // without logging in. On successful login — from the Q1 gate OR the footer
  // "Sign in" link on any other question — always check skip-eligibility
  // first (same as Variant A checking as soon as login is known), then
  // recompute the next page with logged-in skip idxs so health_condition
  // becomes reachable (guest path had it gated out).
  const dismissSignInModal = async (afterLogin = false) => {
    setShowSignInModal(false);
    setSignInModalError(null);
    const pending = pendingAfterSignInRef.current;
    pendingAfterSignInRef.current = null;

    if (afterLogin) {
      const navigated = await skipQuestionnaireRef.current?.submit();
      if (navigated) return;

      // Not skip-eligible → advance into the flow. Make sure we're off the
      // intro (an OAuth "additional info" reload can leave the modal open over
      // the Start screen) so the next question is actually shown.
      setShowIntro(false);

      // Login from the HL-11800 email step (its own "Sign in to skip" link): a
      // signed-in user must not see the step, so replace it with its continuation.
      // A non-page `next` is pushed as is — recomputing from `currentPage` (the
      // consent page) would re-push the Health Summary instead. Arm B's `next`
      // is the consent page: fall through so health_condition (logged-in only)
      // is recomputed into the path like any other login.
      if (currentScreen.type === "contactDetails" && currentScreen.next.type !== "page") {
        pushScreen(currentScreen.next);
        return;
      }

      // submit() failing can mean "not eligible" or an error; only drop health_condition
      // for the first, so an error leaves it on screen to retry with a tap.
      const loggedInSkips = [
        ...consentSkipIdxs,
        ...getAuthGatedSkipIdxs(flattenedQuestionnaire, true, skipEligibleRef.current),
      ];
      const next = computeNextPage(
        currentPage,
        flattenedQuestionnaire,
        questionnaireAttributes.answersSelectedMap,
        loggedInSkips
      );
      // Plain toScreen, not resolveNextScreen: `isLoggedIn` in this closure is
      // still the pre-login value, and a signed-in user must never see the email step.
      pushScreen(toScreen(next));
      return;
    }

    if (!pending) return;
    pushScreen(pending);
  };

  // The health-protected modal auto-opens on its own once on arrival at Q1
  // (see the isFirstQuestionPage effect further down), so a successful login
  // just needs to dismiss the sign-in modal (and flush any deferred nav).
  const handleSignInSuccess = () => {
    dismissSignInModal(true);
  };

  // ---- navigate back ----
  // Always one step. Screens reached by a tap have their own history entry:
  // browser history.back() → popstate trims screenHistory by one. Screens the
  // current entry only covers (a resumed or signed-in start, see
  // coverHistoryUpTo) have none, so step back in-app and re-stamp the entry —
  // traversing would leave the funnel. On the first question, back returns to
  // the intro (depth 0).
  // Do NOT call navigate(-1)/raw history.back from random call sites — that
  // used to skip the stack and land on the pre-funnel route ("start").
  const handleBack = () => {
    if (showIntro) return;
    const state = window.history.state;
    const depth = screenHistory.length - 1;
    if (typeof state?.variantBCoveredFrom === "number" && depth >= state.variantBCoveredFrom) {
      window.history.replaceState({ ...state, variantBDepth: depth }, "", window.location.href);
      if (depth === 0) setShowIntro(true);
      else setScreenHistory((prev) => prev.slice(0, depth));
      return;
    }
    // depth 0 = intro is still in the session history stack after onStart's pushState.
    window.history.back();
  };

  // ---- submit ----
  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    const loaderMinUntil = Date.now() + MIN_SUBMIT_LOADING_MS;

    const revealedForSubmit = computeRevealedForSubmit(
      flattenedQuestionnaire,
      questionnaireAttributes.answersSelectedMap,
      navSkipIdxs
    );

    let bundled = bundleQuestionnaireForSending(
      flattenedQuestionnaire,
      questionnaireAttributes.answersSelectedMap,
      revealedForSubmit
    );
    bundled = formatQuestionnaire(bundled, questionnaireAttributes.answersSelectedMap);

    const isBlocking = isQuestionaireBlocking(
      bundled,
      questionnaireAttributes.answersSelectedMap
    );

    const allFiles = Object.entries(filesMap).flatMap(([qId, qFiles]) =>
      (qFiles as any[]).map((f) => ({ ...f, question_id: Number(qId) }))
    );

    // HL-11440: weight_loss_3 only changes the questionnaire; the cart and everything
    // after it (select treatment, plan, shipping, order) run as weight_loss_2.
    const cartMention = getCartMention(mention);

    try {
      const res = await http.postMultiPartsToServer(
        `questionnaire/${country}/${language}/${cartMention}?${searchParams.toString()}`,
        {
          questionnaire: bundled,
          isBlocking,
          country,
          language,
          mention: cartMention,
          allowOnlyPrescription: country?.toLowerCase() === "fr",
          isExpressReorder: false,
          selectedConsultationType: searchParams.get("ct") || "text",
          customerVerificationRequired: searchParams.get("cv") === "true",
          fileQuestionIds: allFiles.map((f: any) => f.question_id),
        },
        allFiles,
        [http.MIDDLEWARES.CUSTOMER_ID, http.MIDDLEWARES.CART_ORDER]
      );

      // HL-12122: last question submitted and saved.
      MainTracker.track("custom", "questionnaire_completed", { mention, is_blocking: Boolean(isBlocking) });

      saveCart({
        id: res.data.cartID,
        token: res.data.cartToken,
        mention: cartMention!,
        mixpanel_title: res.data.catalog.mixpanel_title,
        products: [],
        timekitBooking: null,
        consultation_type: (searchParams.get("ct") as "video" | "text") || "text",
        customer_verification_type: res.data.customerVerificationType,
        should_id_verify: res.data.shouldIDVerify,
      });

      if (categoryID && typeof country === "string") {
        markPendingClear(country, categoryID);
      }

      trackGTM("custom", "ga4_auto_event", {
        event_name: "medical_questions_submit",
        funnel_step: "funnel_Step_4",
        location: "3",
        subcategory: mention,
      });

      // Everything the entry URL carried travels on: catalog_id preselects the
      // treatment on the next page, and the campaign params are what the funnel
      // is attributed by. Only the OAuth callback params are dropped, matching
      // Questionnaire.tsx — as does /evaluation, which takes none.
      const forwardedSearch = getForwardableFunnelSearchString(searchParams);

      await new Promise((resolve) => setTimeout(resolve, Math.max(0, loaderMinUntil - Date.now())));

      if (isBlocking) {
        navigate({ pathname: `/evaluation/${country}/${language}` });
      } else if (res.data.shouldIDVerify && !isPostCheckoutVerificationCountry(country)) {
        // GB/CH verify later (registration / plan step): the verification page continues to
        // /shipping, so sending them there now would skip treatment and plan selection.
        navigate({
          pathname: `/customer-verification/${country}/${language}`,
          search: forwardedSearch,
        });
      } else {
        navigate({
          pathname: `/select_treatment/${country}/${language}/${cartMention}`,
          search: forwardedSearch,
        });
      }
    } catch {
      trackQuestionError("network");
      setSubmitting(false);
      // Drop the loading page too, or a failed submit strands the user on it.
      setShowSubmitLoading(false);
    }
  };

  // ---- progress ----
  // total = visited question pages + remaining reachable question pages given
  // the current answers — a live estimate, since later answers can reveal/hide
  // branches. Interstitial screens (weight-projection, real-results,
  // health-summary) aren't questions, so they don't get their own slot here —
  // they mirror the question they're attached to (see stepCurrent below),
  // which keeps this denominator meaning exactly "questions", stable, and
  // immune to permanently inflating every time an interstitial is passed.
  const currentStepNumber = pageHistory.length;
  const totalStepsEstimate =
    currentStepNumber +
    estimateRemainingPages(
      currentPage,
      flattenedQuestionnaire,
      questionnaireAttributes.answersSelectedMap,
      navSkipIdxs
    );

  // ---- render helpers ----
  const dangerSignificancesRefsDispatch = (_action: "add" | "remove", _ref: any) => {};

  const renderSpecialPage = () => {
    if (specialPage === "blocked-contraindication") {
      return (
        <div className="flex flex-col items-center justify-center min-h-[60vh] p-6 text-center">
          <ShellScopedId base="wl2_blocked_contraindication_title">
            {(titleId) => (
              <h2 id={titleId} className="text-xl font-semibold mb-4">
                Based on your answers, we are unable to proceed with your consultation.
              </h2>
            )}
          </ShellScopedId>
          <p className="text-gray-600 mb-6">
            {t(language, "contraindication_blocked_body") || "Please speak to your GP for personalised advice."}
          </p>
          <ShellScopedId base="wl2_blocked_contraindication_back">
            {(backId) => (
              <button
                id={backId}
                className="underline text-sm text-gray-500 cursor-pointer hover:text-gray-700 transition"
                onClick={handleBack}
              >
                Go back
              </button>
            )}
          </ShellScopedId>
        </div>
      );
    }
    return null;
  };

  const renderCurrentPage = () => {
    const pageContent = getPageContent(currentPage, flattenedQuestionnaire);
    if (!pageContent) return null;

    const { answerGroup, questionType, question } = pageContent;

    if (questionType === "bmi_question") {
      return (
        <BMISliderSection
          question={question}
          handleRef={questionnaireAttributes.handleRef}
          formData={questionnaireAttributes.answersSelectedMap}
          onTextChange={onTextChange}
          onReveal={noOpReveal}
          onHide={noOpHide}
          dangerSignificancesRefsDispatch={dangerSignificancesRefsDispatch}
          answersSelectedMap={questionnaireAttributes.answersSelectedMap}
          questionnaireValidationObject={questionnaireAttributes.questionnaireValidationObject}
          validationAttempt={bmiValidationAttempt}
          mention={mention}
          disabled={false}
          language={language}
          flattenedQuestionnaire={flattenedQuestionnaire}
        />
      );
    }

    return (
      <RenderAnswerGroup
        answersGroup={answerGroup}
        onTextChange={onTextChange}
        onAnswerClick={onAnswerClick}
        onHide={noOpHide}
        onReveal={noOpReveal}
        questionnaireValidationObject={questionnaireAttributes.questionnaireValidationObject}
        answersSelectedMap={questionnaireAttributes.answersSelectedMap}
        disabled={false}
        files={filesMap[question.id] || []}
        setFiles={((files: any) => setFilesMap((prev: any) => ({ ...prev, [question.id]: files }))) as any}
        language={language}
        category={mention}
        showUploadError={false}
        isVariantB={true}
      />
    );
  };

  // HL-12029 pop-up body: the question's own answer controls wired to the regular
  // handlers, minus the page-level navigation (no one-tap advance, no Continue).
  const renderEditableAnswerGroup = (content: EditablePageContent) => {
    const { answerGroup, question, questionType } = content;
    if (!answerGroup?.recipe?.id || questionType === "bmi_question") return null;
    const type = String(answerGroup.recipe.type || "");
    const errorMode = questionnaireAttributes.questionnaireValidationObject[answerGroup.recipe.id] === false;
    if (type === "radio_buttons") {
      return (
        <SingleSelectAnswers
          answersGroup={answerGroup}
          answersSelectedMap={questionnaireAttributes.answersSelectedMap}
          onSelect={(child: any) => onAnswerClick(child, true)}
          errorMode={errorMode}
        />
      );
    }
    if (type.includes("check_list")) {
      return (
        <MultiSelectAnswers
          answersGroup={answerGroup}
          answersSelectedMap={questionnaireAttributes.answersSelectedMap}
          onToggle={(child: any) => onAnswerClick(child)}
          errorMode={errorMode}
        />
      );
    }
    return (
      <RenderAnswerGroup
        answersGroup={answerGroup}
        onTextChange={onTextChange}
        onAnswerClick={onAnswerClick}
        onHide={noOpHide}
        onReveal={noOpReveal}
        questionnaireValidationObject={questionnaireAttributes.questionnaireValidationObject}
        answersSelectedMap={questionnaireAttributes.answersSelectedMap}
        disabled={false}
        files={filesMap[question.id] || []}
        setFiles={((files: any) => setFilesMap((prev: any) => ({ ...prev, [question.id]: files }))) as any}
        language={language}
        category={mention}
        showUploadError={false}
        isVariantB={true}
        isBirthdayQuestion={false}
      />
    );
  };

  const renderSummaryEditModals = () => {
    const { bmiQuestion, heightNode, weightNode, birthdayNode } = getHealthProfileRefs();
    const { comorbidity: comorbidityThreshold, minimum: minimumThreshold } = extractBmiThresholds(bmiQuestion);
    const birthdayId = birthdayNode?.recipe?.id;
    const editingContents =
      editingQuestionIdx === null
        ? []
        : editablePageContents(editingQuestionIdx, questionnaireAttributes.answersSelectedMap);
    const [editingRoot, ...editingFollowUps] = editingContents;

    return (
      <>
        <HealthProfileEditModal
          isOpen={showHealthProfileEdit}
          onClose={() => setShowHealthProfileEdit(false)}
          onSave={saveHealthProfileEdit}
          language={language}
          birthday={birthdayId ? questionnaireAttributes.answersSelectedMap[birthdayId] ?? null : null}
          heightCm={Number(questionnaireAttributes.answersSelectedMap[heightNode?.recipe?.id]) || null}
          weightKg={Number(questionnaireAttributes.answersSelectedMap[weightNode?.recipe?.id]) || null}
          heightRule={heightNode?.recipe?.validation_rule}
          weightRule={weightNode?.recipe?.validation_rule}
          inputLimits={usesBmiInputLimits(bmiQuestion)}
          comorbidityThreshold={comorbidityThreshold}
          minimumThreshold={minimumThreshold}
          hasComorbidity={computeHasComorbidity(bmiQuestion, flattenedQuestionnaire, questionnaireAttributes.answersSelectedMap)}
        />
        {editingRoot && (
          <AnswerEditModal
            isOpen
            onClose={discardAnswerEdit}
            onSave={saveAnswerEdit}
            titleHtml={highlightRevealingDiseaseName(editingRoot.text, editingRoot)}
            language={language}
          >
            {renderEditableAnswerGroup(editingRoot)}
            {editingFollowUps.map((content) => (
              <div key={content.answerGroup.recipe.id} className="flex flex-col gap-3">
                <p
                  className="font-['Poppins'] text-[15px] font-medium text-[#08080a] m-0"
                  dangerouslySetInnerHTML={{ __html: highlightRevealingDiseaseName(content.text, content) || "" }}
                />
                {renderEditableAnswerGroup(content)}
              </div>
            ))}
          </AnswerEditModal>
        )}
      </>
    );
  };

  const isLastPage =
    computeNextPage(
      currentPage,
      flattenedQuestionnaire,
      questionnaireAttributes.answersSelectedMap,
      navSkipIdxs
    ) === null;

  const isBmiQuestionPage =
    currentPage.subIdx === null &&
    flattenedQuestionnaire[currentPage.questionIdx]?.question_type === "bmi_question";

  // Gray-out Weiter when BMI is below the hard threshold or an entry is unrealistic (message already on page).
  const isBmiContinueDisabled = isBmiQuestionPage && (isBmiBlocked() || isBmiInputOutOfRange());

  // ---- Q1 — hero + card layout + health modal ----
  // The first page shown after the intro (leading "title" pages are skipped),
  // identified by the history stack holding a single entry.
  const firstPage = firstAnswerablePage(flattenedQuestionnaire, navSkipIdxs);
  const isFirstQuestionPage =
    !specialPage &&
    pageHistory.length === 1 &&
    currentPage.subIdx === null &&
    currentPage.questionIdx === firstPage.questionIdx;

  // Auto-open the health-protected reassurance exactly once on Q1, but only
  // for paid/campaign traffic (utm_* / gclid / fbclid). Organic / direct
  // visitors skip it. Guarded on `!loading` so it can't fire (and burn the
  // "once" ref) while screenHistory's placeholder first page precedes the
  // real questionnaire data arriving.
  useEffect(() => {
    if (loading || !isFirstQuestionPage || healthModalShownRef.current) return;

    healthModalShownRef.current = true;

    const params = searchParams;
    const fromPaidOrCampaign = !!(
      params.get("utm_source") ||
      params.get("utm_medium") ||
      params.get("utm_campaign") ||
      params.get("utm_content") ||
      params.get("utm_term") ||
      params.get("gclid") ||
      params.get("fbclid")
    );
    if (!fromPaidOrCampaign) return;

    setShowHealthModal(true);
  }, [loading, isFirstQuestionPage, searchParams]);

  const dismissHealthModal = () => {
    setShowHealthModal(false);
  };

  // SkipQuestionnaireModal is mounted headless in Variant B: eligibility is
  // prefetched the same way as Variant A, but the popup UI is suppressed —
  // health_condition "No / nothing changed" calls submit() via the ref instead.

  // ---- render ----

  // Sign-in modal + Google sign-up completion popup. Rendered on the intro and
  // loading branches as well as the question pages: a brand-new patient coming
  // back from the Google redirect has no saved answers, so they land on the
  // intro — and the popup that finishes their sign-up must be there too.
  const authModals = (
    <>
      <SignInModal
        isOpen={showSignInModal}
        onClose={() => dismissSignInModal(false)}
        onSkip={() => dismissSignInModal(false)}
        onLoginSuccess={handleSignInSuccess}
        externalError={signInModalError}
        language={language!}
        country={country!}
      />
      {/* Sibling of SignInModal, never its child — see showOAuthSignupCompletion. */}
      <OAuthSignupCompletion
        isOpen={showOAuthSignupCompletion && !isLoggedIn}
        onClose={() => setShowOAuthSignupCompletion(false)}
        language={language!}
        country={country!}
        params={oauthSignupParams}
        // saveCustomer flips isLoggedIn; the delayed-login effect above then
        // rebuilds the history as signed in (health_condition unlocked, or the
        // email step's continuation on the HL-11800 return leg), leaves the
        // intro and runs the skip-eligibility check — the same path an existing
        // Google account takes when OAuthCallback's saveCustomer lands after mount.
        onCompleteSignup={() => setShowOAuthSignupCompletion(false)}
        onFailureSignup={() => {
          // Let the user retry (password or Google again) with the error visible.
          setShowOAuthSignupCompletion(false);
          setSignInModalError(
            translator.translate(language, "default", "StickyNotification/oauth_failed")
          );
          setShowSignInModal(true);
        }}
      />
    </>
  );

  if (showIntro) {
    return inFunnel(
      <>
        <IntroScreen
          language={language}
          mention={mention}
          onStart={() => {
            window.history.pushState({ variantBDepth: screenHistory.length }, "", window.location.href);
            setShowIntro(false);
          }}
        />
        {authModals}
      </>
    );
  }

  // The popup sits above the spinner (z 1000 vs 999); finishing the sign-up
  // while the GET is in flight is fine — the load effect reads live auth.
  if (loading) {
    return inFunnel(
      <>
        <LoadingModal />
        {authModals}
      </>
    );
  }

  if (error) {
    return inFunnel(
      <>
        <HeaderController showSteps={false} stage="Questionnaire" isCannabis={false} />
        <ServerError status={500} />
      </>
    );
  }

  // Health Summary "Continue" fires the submit from the summary screen, which stays current until navigation.
  if (showSubmitLoading) {
    return inFunnel(<LoadingPage language={language} centerContent={funnel === "wl3"} />);
  }

  let pageBody: React.ReactNode;

  // Shared shell for the full-page interstitials below: same QuestionFrame
  // chrome (no hero, back/sign-in header) and trailing CTA, differing only in
  // title, body content, and CTA behavior.
  // A follow-up page is a recipe_answers row under its parent question, so it
  // carries both ids; the parent page already uses `question_{parent}` alone.
  const questionDomIdFor = (page: PageDescriptor): string | undefined => {
    const content = getPageContent(page, flattenedQuestionnaire);
    const questionId = content?.question?.id;
    if (!questionId) return undefined;
    if (page.subIdx === null) return `question_${questionId}`;
    const subId = content?.answerGroup?.recipe?.id;
    return subId ? `question_${questionId}_sub_${subId}` : `question_${questionId}`;
  };

  // Interstitials have no recipe row, so each passes its own ids.
  const renderInterstitial = ({
    titleHtml,
    body,
    ctaLabel,
    onCta,
    ctaClassName,
    pageId,
    ctaId,
    titleClassName,
    footerNote,
    showSignInLink = !customerState?.customerID,
  }: {
    titleHtml?: string;
    body: React.ReactNode;
    ctaLabel: string;
    onCta: () => void;
    ctaClassName?: string;
    pageId: string;
    ctaId: string;
    titleClassName?: string;
    footerNote?: React.ReactNode;
    /** HL-12029 arm B hides the guest sign-in link on the Health Summary. */
    showSignInLink?: boolean;
  }) => (
    <QuestionFrame
      showHero={false}
      pageId={pageId}
      stepCurrent={currentStepNumber}
      stepTotal={totalStepsEstimate}
      titleHtml={titleHtml}
      titleClassName={titleClassName}
      footerNote={footerNote}
      onBack={handleBack}
      onSignIn={() => setShowSignInModal(true)}
      showSignInLink={showSignInLink}
      language={language}
      cta={
        <PrimaryCtaButton id={ctaId} className={ctaClassName} label={ctaLabel} onClick={onCta} disabled={submitting} />
      }
    >
      {body}
    </QuestionFrame>
  );

  // Interstitial overlay shown right after the BMI question, before advancing
  // to the next real page — checked ahead of isFirstQuestionPage so it still
  // takes priority if the BMI question happens to be Q1.
  if (showWeightProjection) {
    const bmiQuestion = flattenedQuestionnaire[currentPage.questionIdx];
    const weightAnswerId = bmiQuestion?.answersGroup?.children?.[0]?.recipe?.id;
    const currentWeightKg =
      Number(questionnaireAttributes.answersSelectedMap[weightAnswerId]) || 0;

    pageBody = renderInterstitial({
      pageId: "wl2_weight_projection_page",
      ctaId: "wl2_weight_projection_continue",
      titleHtml: t(language, "weight_projection_title"),
      titleClassName: "font-['Poppins'] font-semibold text-[#f08e00] text-[32px] leading-[38px] text-center mb-0",
      body: <WeightLossProjectionPage language={language} currentWeightKg={currentWeightKg} />,
      footerNote: <WeightLossProjectionDisclaimer language={language} />,
      ctaClassName: "md:max-w-[520px] md:mx-auto",
      ctaLabel: isLastPage ? t(language, "submit_button_text") : t(language, "q_continue"),
      onCta: () => handleNext(),
    });
  }

  // Interstitial overlay shown right after the biological-gender question,
  // before advancing to the next real page — same pattern as showWeightProjection.
  else if (showRealResults) {
    pageBody = renderInterstitial({
      pageId: "wl2_real_results_page",
      ctaId: "wl2_real_results_continue",
      titleHtml: `<span class="hidden lg:inline" style="background-image: linear-gradient(193.9deg, rgb(255,185,9) 0.8%, rgb(225,114,0) 94.8%); -webkit-background-clip: text; background-clip: text; color: transparent; font-size: 34px;">${t(language, "real_results_title")}</span><span class="lg:hidden" style="color: #08080a; font-size: 24px; letter-spacing: 1px;">${t(language, "real_results_title")}</span>`,
      body: <RealResultsPage language={language} gender={currentScreen.type === "realResults" ? currentScreen.gender : undefined} />,
      ctaLabel: isLastPage ? t(language, "submit_button_text") : t(language, "q_continue"),
      onCta: () => handleNext(),
    });
  }

  // Interstitial overlay shown right after "Why do you want to lose weight?" —
  // same pattern as showRealResults, resumes real navigation via handleNext().
  else if (showSocialProof) {
    pageBody = renderInterstitial({
      pageId: "wl2_social_proof_page",
      ctaId: "wl2_social_proof_continue",
      body: <SocialProofPage language={language} />,
      ctaLabel: t(language, "social_proof_start_button"),
      onCta: () => handleNext(),
    });
  }

  // Interstitial overlay shown right before the final submit — recaps the
  // answers the user gave. Same pattern as showWeightProjection.
  else if (showHealthSummary) {
    // HL-12029: answers can be edited from here (pencil pop-ups + health profile modal).
    // While a pop-up is open the summary keeps showing the pre-edit answers (AC-4).
    const summaryAnswers = answersForSummary();

    const { heightNode, weightNode, birthdayNode } = getHealthProfileRefs();
    const heightId = heightNode?.recipe?.id;
    const weightId = weightNode?.recipe?.id;
    const heightCm = Number(summaryAnswers[heightId]) || null;
    const weightKg = Number(summaryAnswers[weightId]) || null;
    const bmi = heightCm && weightKg ? calculateBMI(weightKg, heightCm) : null;
    const goalWeightKg = weightKg ? projectedGoalWeight(weightKg) : null;

    const birthdayId = birthdayNode?.recipe?.id;
    const ageYears = calculateAge(summaryAnswers[birthdayId]);

    const healthSummaryEntries = getHealthSummaryEntries(
      flattenedQuestionnaire,
      summaryAnswers,
      language
    );

    pageBody = renderInterstitial({
      pageId: "wl2_health_summary_page",
      ctaId: "wl2_health_summary_continue",
      // HL-12029 §2: no sign-in link on the summary page.
      showSignInLink: false,
      body: (
        <HealthProfileSummaryPage
          language={language}
          ageYears={ageYears}
          heightCm={heightCm}
          weightKg={weightKg}
          bmi={bmi}
          goalWeightKg={goalWeightKg}
          healthSummaryEntries={healthSummaryEntries}
          onEditGoal={handleSummaryEditGoal}
          onEditEntry={handleSummaryEditEntry}
        />
      ),
      // Copy per Figma 5713:20026.
      ctaLabel: t(language, "health_summary_edit_continue"),
      onCta: async () => {
        MainTracker.track("custom", SUMMARY_CONTINUE_CLICKED_EVENT, { ...summaryEventProps(), edited: summaryEdited });
        setShowSubmitLoading(true);
        await handleSubmit();
      },
    });
  }

  // HL-11800 / HL-11986 guest-only email capture, right before Legal Consent
  // (the design arm A/B/C only changes its look). Continue (after the lead
  // POST settles) and Skip both advance to the screen carried in `next`; the
  // real submit still fires from the Health Summary. Owns its QuestionFrame because
  // the form state has to reach both the input and the CTA slot (floated on
  // mobile).
  else if (currentScreen.type === "contactDetails") {
    const continueTo = currentScreen.next;
    pageBody = (
      <ContactDetailsPage
        language={language}
        country={country!}
        mention={mention!}
        categoryID={categoryID}
        alreadySubmitted={emailGiven}
        stepCurrent={currentStepNumber}
        stepTotal={totalStepsEstimate}
        onBack={handleBack}
        onSignIn={() => setShowSignInModal(true)}
        onDone={async (outcome) => {
          if (outcome === "submitted") {
            markWlEmailGiven();
            setEmailGiven(true);
          }
          pushScreen(continueTo);
        }}
      />
    );
  }

  // Styled first-question page (hero + card) + health modal.
  else if (isFirstQuestionPage) {
    const pageContent = getPageContent(currentPage, flattenedQuestionnaire);
    const answerGroup = pageContent?.answerGroup;
    const questionType = pageContent?.questionType;
    const isHealthCondition = questionType === "health_condition";
    // UK question pages show no hero image (desktop or mobile).
    const showQuestionHero = !isHealthCondition && !isUkFunnel;
    const isSingleSelect = answerGroup?.recipe?.type === "radio_buttons";
    const isMultiSelect = String(answerGroup?.recipe?.type || "").includes("check_list");
    // health_condition renders its own HealthConditionAnswers on the same radio group.
    const singleSelectAnswers =
      isSingleSelect && !isHealthCondition ? singleSelectAnswersFor(answerGroup, pageContent?.helpBlock) : null;

    pageBody = (
      <QuestionFrame
        questionDomId={questionDomIdFor(currentPage)}
        bottomContent={singleSelectAnswers?.pinned}
        heroDesktop={showQuestionHero ? HERO_DESKTOP : undefined}
        heroMobile={showQuestionHero ? HERO_MOBILE : undefined}
        discountBadgeDesktop={showQuestionHero ? DISCOUNT_BADGE_DESKTOP : undefined}
        discountBadgeMobile={showQuestionHero ? DISCOUNT_BADGE_MOBILE : undefined}
        showHero={showQuestionHero}
        stepCurrent={1}
        stepTotal={totalStepsEstimate}
        titleHtml={
          isHealthCondition
            ? pageContent?.text
            : questionType === "bmi_question"
              ? t(language, "bmi_slider_title")
              : pageContent?.text
        }
        // health_condition shows help_block in its own info card body.
        subtitleHtml={isHealthCondition ? undefined : pageContent?.helpBlock}
        whyWeAskBlock={isHealthCondition ? undefined : pageContent?.whyWeAskBlock}
        onWhyWeAskClick={() => {
          setWhyWeAskContent(pageContent?.whyWeAskBlock);
          setIsWhyWeAskOpen(true);
        }}
        onBack={handleBack}
        onSignIn={() => setShowSignInModal(true)}
        showSignInLink={!customerState?.customerID}
        language={language}
        cta={
          // radio_buttons / health_condition advance on tap — no trailing Continue.
          // check_list keeps Continue (multi-select).
          isHealthCondition || isSingleSelect ? undefined : (
            <PrimaryCtaButton
              id="wl2_question_continue"
              className="mt-6"
              label={isLastPage ? t(language, "submit_button_text") : t(language, "q_continue")}
              onClick={isBmiQuestionPage
                  ? handleBmiContinue
                  : () => handleNext()}
              disabled={submitting || isBmiContinueDisabled}
            />
          )
        }
      >
        {isHealthCondition ? (
          <HealthConditionAnswers
            answersGroup={answerGroup}
            answersSelectedMap={questionnaireAttributes.answersSelectedMap}
            onSelect={handleHealthConditionSelect}
            errorMode={
              answerGroup?.recipe?.id
                ? questionnaireAttributes.questionnaireValidationObject[answerGroup.recipe.id] === false
                : false
            }
            language={language}
            infoHtml={pageContent?.helpBlock}
          />
        ) : isSingleSelect ? (
          singleSelectAnswers?.inline
        ) : isMultiSelect ? (
          <MultiSelectAnswers
            answersGroup={answerGroup}
            answersSelectedMap={questionnaireAttributes.answersSelectedMap}
            onToggle={(child: any) => {
              onAnswerClick(child);
              scheduleSave();
            }}
            errorMode={questionnaireAttributes.questionnaireValidationObject[answerGroup.recipe.id] === false}
          />
        ) : questionType === "bmi_question" ? (
          <BMISliderSection
            question={pageContent!.question}
            handleRef={questionnaireAttributes.handleRef}
            formData={questionnaireAttributes.answersSelectedMap}
            onTextChange={onTextChange}
            onReveal={noOpReveal}
            onHide={noOpHide}
            dangerSignificancesRefsDispatch={dangerSignificancesRefsDispatch}
            answersSelectedMap={questionnaireAttributes.answersSelectedMap}
            questionnaireValidationObject={questionnaireAttributes.questionnaireValidationObject}
            validationAttempt={bmiValidationAttempt}
            mention={mention}
            disabled={false}
            language={language}
            flattenedQuestionnaire={flattenedQuestionnaire}
          />
        ) : (
          <RenderAnswerGroup
            answersGroup={answerGroup}
            onTextChange={onTextChange}
            onAnswerClick={onAnswerClick}
            onHide={noOpHide}
            onReveal={noOpReveal}
            questionnaireValidationObject={questionnaireAttributes.questionnaireValidationObject}
            answersSelectedMap={questionnaireAttributes.answersSelectedMap}
            disabled={false}
            files={filesMap[pageContent!.question.id] || []}
            setFiles={((files: any) => setFilesMap((prev: any) => ({ ...prev, [pageContent!.question.id]: files }))) as any}
            language={language}
            category={mention}
            showUploadError={false}
            isVariantB={true}
          />
        )}
      </QuestionFrame>
    );
  } else {
  const pageContent = specialPage ? null : getPageContent(currentPage, flattenedQuestionnaire);
  const isSingleSelect = !specialPage && pageContent?.answerGroup?.recipe?.type === "radio_buttons";
  const isMultiSelect =
    !specialPage && String(pageContent?.answerGroup?.recipe?.type || "").includes("check_list");
  const questionType = pageContent?.questionType;

  // Final Legal Consent page: styled title + green check-points
  // (from the DB question text) + "view full legal consent" popup + a Continue
  // button that auto-agrees the single consent answer(s) and submits.
  const isLegalConsentPage =
    !specialPage &&
    legalConsentIdx !== null &&
    currentPage.questionIdx === legalConsentIdx &&
    currentPage.subIdx === null;

  if (isLegalConsentPage) {
    // Each consent question folded onto this one page, by role.
    const statementsHtml =
      statementsIdx !== null
        ? flattenedQuestionnaire[statementsIdx]?.text ?? ""
        : pageContent?.text ?? "";
    // Full-legal accordion uses the answer option HTML, not the question body (UK: the question body).
    const fullLegalHtml =
      fullLegalIdx !== null
        ? getConsentAnswerHtml(flattenedQuestionnaire[fullLegalIdx], country?.toUpperCase() === "GB")
        : "";
    // Terms/Privacy summary line — always the translation fallback.
    const summaryHtml = t(language, "legal_consent_terms_summary");

    pageBody = (
      <QuestionFrame
        showHero={false}
        fitViewport
        pageId="wl2_legal_consent_page"
        questionDomId={questionDomIdFor(currentPage)}
        stepCurrent={currentStepNumber}
        stepTotal={totalStepsEstimate}
        titleHtml={t(language, "legal_consent_title")}
        onBack={handleBack}
        onSignIn={() => setShowSignInModal(true)}
        showSignInLink={!customerState?.customerID}
        language={language}
        cta={
          <PrimaryCtaButton
            id="wl2_legal_consent_continue"
            label={t(language, "q_continue")}
            onClick={agreeAndContinue}
            disabled={submitting}
          />
        }
      >
        <style>{`.consent-content li, .consent-content p { word-break: break-word; overflow-wrap: break-word; }`}</style>
        {/* The consent text is long and fully CMS-driven, so on a short phone
            it would push the "Continue" CTA below the fold. Wrap the whole
            consent body in one scroll region (rather than capping each block
            separately, which would fight over several scrollbars) so the CTA
            stays reachable while the legal text scrolls inside. */}
        <ShellScopedId base="wl2_legal_consent_body">
          {(bodyId) => (
        <div
          id={bodyId}
          // HL-12357: on a WL3 phone the box runs down to the sign-in / Continue area instead of
          // stopping halfway (320px ≈ header + title above, sign-in + floating CTA below).
          className={`w-full overflow-y-auto overscroll-contain md:max-h-[52dvh] ${
            funnel === "wl3" ? "max-h-[max(46dvh,calc(100dvh-320px))]" : "max-h-[46dvh]"
          }`}
        >
        {/* Consent statements — the DB question text, styled as green checks. */}
        <ShellScopedId base="wl2_legal_consent_statements">
          {(statementsId) => (
        <div
          id={statementsId}
          className="consent-content mb-6 font-['Roboto'] [&_ul]:list-none [&_ul]:p-0 [&_ul]:m-0 [&_ul]:flex [&_ul]:flex-col [&_ul]:gap-[12px] [&_li]:relative [&_li]:pl-7 [&_li]:text-[13px] [&_li]:leading-[1.45] [&_li]:text-[#08080a] [&_li]:before:content-['✓'] [&_li]:before:absolute [&_li]:before:left-0 [&_li]:before:top-0 [&_li]:before:font-bold [&_li]:before:text-[#269C6E] [&_p]:text-[13px] [&_p]:leading-[1.5] [&_p]:text-[#08080a] [&_p]:mt-4 [&_a]:text-[#218c87] [&_a]:underline"
          dangerouslySetInnerHTML={{ __html: statementsHtml }}
        />
          )}
        </ShellScopedId>

        {/* Terms & Conditions / Privacy Policy summary line. */}
        {summaryHtml && (
          <ShellScopedId base="wl2_legal_consent_summary">
            {(summaryId) => (
          <div
            id={summaryId}
            className="consent-content mb-6 font-['Roboto'] text-[13px] leading-[1.5] text-[#08080a] font-medium [&_a]:text-[#218c87] [&_a]:underline"
            dangerouslySetInnerHTML={{ __html: summaryHtml }}
          />
            )}
          </ShellScopedId>
        )}

        {/* View full legal consent → inline accordion (full AGB/Datenschutz text). */}
        {fullLegalIdx !== null && fullLegalHtml && (
          <div className="mb-6">
            <ShellScopedId base="wl2_legal_consent_view_full">
              {(viewFullId) => (
            <button
              id={viewFullId}
              onClick={() => {
                setShowFullLegal((v) => {
                  if (!v) {
                    // Opening: after React renders the expanded block, scroll it into view.
                    const id = viewFullId;
                    if (id) requestAnimationFrame(() => requestAnimationFrame(() => {
                      document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
                    }));
                  }
                  return !v;
                });
              }}
              aria-expanded={showFullLegal}
              className="w-full flex items-center justify-between rounded-[10px] border border-solid border-[#d9dbe0] px-4 py-[14px] cursor-pointer hover:bg-[#f5f6f7] transition"
              style={{ background: "transparent" }}
            >
              <span className="font-['Roboto'] font-medium text-[13px] text-[#08080a]">
                {t(language, "legal_consent_view_full")}
              </span>
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                className="text-[#08080a] transition-transform"
                style={{ transform: showFullLegal ? "rotate(180deg)" : "none" }}
              >
                <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
              )}
            </ShellScopedId>
            {showFullLegal && (
              <ShellScopedId base="wl2_legal_consent_full_text">
                {(fullTextId) => (
              <div
                id={fullTextId}
                // No inner max-height/scroll: the whole consent body already
                // scrolls in the wrapper above, and nesting a second scroller
                // here makes the expanded legal text awkward to read.
                className="consent-content mt-2 rounded-[10px] border border-solid border-[#d9dbe0] px-4 py-3 font-['Roboto'] text-[12px] leading-[1.5] text-[#3a3a3a] [&_a]:text-[#218c87] [&_a]:underline"
                dangerouslySetInnerHTML={{ __html: fullLegalHtml }}
              />
                )}
              </ShellScopedId>
            )}
          </div>
        )}
        </div>
          )}
        </ShellScopedId>
      </QuestionFrame>
    );
  } else {
    const isHealthCondition = questionType === "health_condition";
    const singleSelectAnswers = isSingleSelect && !isHealthCondition
      ? singleSelectAnswersFor(pageContent!.answerGroup, pageContent?.helpBlock)
      : null;
    pageBody = (
      <QuestionFrame
        showHero={false}
        // Natural-height layout hug: card sizes to content (no inner scroll).
        // fitViewport is only on legal consent above.
        pageId={specialPage ? "wl2_blocked_contraindication_page" : "wl2_question_page"}
        questionDomId={specialPage ? undefined : questionDomIdFor(currentPage)}
        stepCurrent={currentStepNumber}
        stepTotal={totalStepsEstimate}
        titleHtml={
          questionType === "bmi_question"
            ? t(language, "bmi_slider_title")
            : highlightRevealingDiseaseName(pageContent?.text, pageContent)
        }
        // health_condition shows help_block in its own info card body.
        subtitleHtml={
          isHealthCondition
            ? undefined
            : highlightRevealingDiseaseName(pageContent?.helpBlock, pageContent)
        }
        whyWeAskBlock={isHealthCondition ? undefined : pageContent?.whyWeAskBlock}
        onWhyWeAskClick={() => {
          setWhyWeAskContent(pageContent?.whyWeAskBlock);
          setIsWhyWeAskOpen(true);
        }}
        isFollowUp={questionType?.startsWith("subquestion_")}
        onBack={handleBack}
        onSignIn={() => setShowSignInModal(true)}
        showSignInLink={!customerState?.customerID}
        language={language}
        bottomContent={singleSelectAnswers?.pinned}
        cta={
          // radio_buttons / health_condition advance on tap — no trailing Continue.
          // check_list (and other types) keep Continue.
          specialPage || isHealthCondition || isSingleSelect
            ? undefined
            : (
              <PrimaryCtaButton
                id="wl2_question_continue"
                label={isLastPage ? t(language, "submit_button_text") : t(language, "q_continue")}
                onClick={isBmiQuestionPage
                  ? handleBmiContinue
                  : () => handleNext()}
                disabled={submitting || isBmiContinueDisabled}
              />
            )
        }
      >
        {specialPage ? (
          renderSpecialPage()
        ) : isHealthCondition ? (
          <HealthConditionAnswers
            answersGroup={pageContent!.answerGroup}
            answersSelectedMap={questionnaireAttributes.answersSelectedMap}
            onSelect={handleHealthConditionSelect}
            errorMode={
              pageContent!.answerGroup?.recipe?.id
                ? questionnaireAttributes.questionnaireValidationObject[pageContent!.answerGroup.recipe.id] === false
                : false
            }
            language={language}
            infoHtml={pageContent?.helpBlock}
          />
        ) : isSingleSelect ? (
          singleSelectAnswers?.inline
        ) : isMultiSelect ? (
          <MultiSelectAnswers
            answersGroup={pageContent!.answerGroup}
            answersSelectedMap={questionnaireAttributes.answersSelectedMap}
            onToggle={(child: any) => {
              onAnswerClick(child);
              scheduleSave();
            }}
            errorMode={questionnaireAttributes.questionnaireValidationObject[pageContent!.answerGroup.recipe.id] === false}
          />
        ) : (
          renderCurrentPage()
        )}
      </QuestionFrame>
    );
  }
  }

  return inFunnel(
    <>
      <ScreenTransitionProvider value={screenTransition}>{pageBody}</ScreenTransitionProvider>
      <SkipQuestionnaireModal
        ref={skipQuestionnaireRef}
        askOncePerSession
        orangeButtons={funnel === "wl3"}
        categoryCatalog={
          categoryID && mention
            ? { id: categoryID, mention: getCartMention(categoryMention || mention)! }
            : null
        }
        language={language}
        country={country}
        skipModals={searchParams.get("skipQuestionnaire")}
        // HL-12059: the popup shows in WL3 too (product decision), even though WL3's own
        // health_condition question follows it after "Yes, something changed".
        onEligibilityChange={handleEligibilityChange}
        // "Yes, something changed": back to the first question so the patient can
        // review from the top. Answers stay selected.
        onHealthChanged={() => {
          // Show Q1 by cutting the visited-pages list to its first page.
          // Don't use history.go(-n): the browser's history count can differ
          // from this list, and going back too far exits the questionnaire.
          window.history.replaceState({ variantBDepth: 1 }, "", window.location.href);
          setScreenHistory((prev) => prev.slice(0, 1));
        }}
      />
      <HealthProtectedModal
        isOpen={showHealthModal}
        onClose={dismissHealthModal}
        language={language}
      />
      <WhyWeAskModal
        isOpen={isWhyWeAskOpen}
        onClose={() => setIsWhyWeAskOpen(false)}
        bodyHtml={whyWeAskContent}
        language={language}
      />
      {renderSummaryEditModals()}
      <BlockerModal
        isOpen={showBlockerModal}
        onClose={() => setShowBlockerModal(false)}
        language={language}
      />
      {authModals}
    </>
  );
};

export default QuestionnaireVariantB;
